import { useState, useEffect, useCallback, useRef } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDate, formatDateTime, localTodayStr } from '../lib/dateFormat';
import {
  Building2, Users, BarChart3, Shield, RefreshCw, ArrowLeft,
  CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronUp,
  Search, TrendingUp, Edit3, Save, X, Plus, Minus, GitBranch,
  CreditCard, Upload, Globe, Phone, Settings,
  Clock, Layers, Trash2, Star, HardDrive, Eye, FlaskConical, Mail, Calendar, Percent
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import CalcInput from '../components/CalcInput';
import { CapacityPanel } from '../components/superadmin/CapacityPanel';

/* ── helpers ──────────────────────────────────────────────────────────────── */
const PLAN_COLORS = {
  trial:       'bg-blue-500/15 text-blue-300 border-blue-500/30',
  basic:       'bg-slate-500/15 text-slate-300 border-slate-500/30',
  standard:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pro:         'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  founders:    'bg-amber-400/20 text-amber-300 border-amber-400/40',
  suspended:   'bg-red-500/15 text-red-300 border-red-500/30',
  grace_period:'bg-amber-500/15 text-amber-300 border-amber-500/30',
  expired:     'bg-red-500/15 text-red-400 border-red-500/30',
};

const STATUS_DOT = {
  active:       'bg-emerald-400',
  trial:        'bg-blue-400',
  grace_period: 'bg-amber-400',
  expired:      'bg-red-400',
  suspended:    'bg-red-600',
  founders:     'bg-amber-400',
};

function BranchGauge({ used, max }) {
  const pct = max === 0 ? 0 : Math.min((used / max) * 100, 100);
  const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
  const dots = max === 0 ? 10 : Math.min(max + (used > max ? used - max : 0), 10);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        {Array.from({ length: dots }).map((_, i) => (
          <div key={i} className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: i < used ? color : '#1e293b', border: `1px solid ${i < used ? color : '#334155'}` }} />
        ))}
        {max === 0 && <span className="text-xs text-slate-500 ml-1">∞</span>}
      </div>
      <span className="text-xs" style={{ color }}>
        {used}/{max === 0 ? '∞' : max} branches
      </span>
    </div>
  );
}

function PlanBadge({ plan }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold uppercase tracking-wide ${PLAN_COLORS[plan] || PLAN_COLORS.basic}`}>
      {plan}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color = 'emerald' }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-slate-400 text-xs font-medium uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 rounded-lg bg-${color}-500/10 flex items-center justify-center`}>
          <Icon size={16} className={`text-${color}-400`} />
        </div>
      </div>
      <div className="text-3xl font-extrabold text-white">{value}</div>
      {sub && <div className="text-slate-500 text-xs mt-1">{sub}</div>}
    </div>
  );
}

/* ── Pricing Panel ───────────────────────────────────────────────────────── */
const PLAN_DISPLAY = {
  starter_monthly:      { name: 'Starter',      interval: 'Monthly',  badge: 'bg-slate-700' },
  starter_annual:       { name: 'Starter',      interval: 'Annual',   badge: 'bg-slate-700' },
  business_monthly:     { name: 'Business',     interval: 'Monthly',  badge: 'bg-emerald-900' },
  business_annual:      { name: 'Business',     interval: 'Annual',   badge: 'bg-emerald-900' },
  professional_monthly: { name: 'Professional', interval: 'Monthly',  badge: 'bg-indigo-900' },
  professional_annual:  { name: 'Professional', interval: 'Annual',   badge: 'bg-indigo-900' },
  enterprise_monthly:   { name: 'Enterprise',   interval: 'Monthly',  badge: 'bg-purple-900' },
  enterprise_annual:    { name: 'Enterprise',   interval: 'Annual',   badge: 'bg-purple-900' },
};

function PhpInput({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-slate-400 text-sm font-mono">₱</span>
      <input
        type="number"
        min="0"
        step="1"
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        className="w-24 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-sm text-white font-mono text-right focus:outline-none focus:border-indigo-500"
        data-testid={testId}
      />
    </div>
  );
}

function PricingPanel() {
  const [plans, setPlans]         = useState([]);
  const [bundles, setBundles]     = useState([]);
  const [planEdits, setPlanEdits] = useState({});
  const [bundleEdits, setBundleEdits] = useState({});
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [hasOverrides, setHasOverrides] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/superadmin/pricing');
      setPlans(r.data.plans || []);
      setBundles(r.data.bundles || []);
      setHasOverrides(r.data.has_overrides || false);
      // Populate edits with current values
      const pe = {};
      (r.data.plans || []).forEach(p => {
        pe[p.id] = {
          amount_php: p.amount_php,
          extra_branch_php: p.extra_branch_php,
          extra_branch_annual_php: p.extra_branch_annual_php,
        };
      });
      setPlanEdits(pe);
      const be = {};
      (r.data.bundles || []).forEach(b => { be[b.id] = { addon_price_php: b.addon_price_php }; });
      setBundleEdits(be);
    } catch { toast.error('Failed to load pricing'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/pricing', {
        plan_overrides: planEdits,
        bundle_overrides: bundleEdits,
      });
      toast.success('Pricing saved — takes effect on next checkout');
      setHasOverrides(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm('Reset all prices to hardcoded defaults?')) return;
    setSaving(true);
    try {
      await api.delete('/superadmin/pricing/reset');
      toast.success('Prices reset to defaults');
      await load();
    } catch { toast.error('Reset failed'); }
    setSaving(false);
  };

  if (loading) return <div className="py-16 text-center text-slate-500">Loading pricing...</div>;

  // Group plans by name for a cleaner table
  const planGroups = {};
  plans.forEach(p => {
    const meta = PLAN_DISPLAY[p.id] || { name: p.display_name, interval: p.interval, badge: 'bg-slate-700' };
    if (!planGroups[meta.name]) planGroups[meta.name] = [];
    planGroups[meta.name].push({ ...p, _meta: meta });
  });

  return (
    <div className="space-y-8" data-testid="pricing-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Pricing Editor</h2>
          <p className="text-slate-400 text-sm mt-0.5">
            Override plan and bundle prices. Changes take effect on the next checkout — existing subscriptions are unaffected.
          </p>
        </div>
        <div className="flex gap-2">
          {hasOverrides && (
            <Button variant="outline" size="sm" onClick={reset} disabled={saving}
              className="border-red-700 text-red-400 hover:bg-red-900/30"
              data-testid="reset-pricing-btn">
              Reset to defaults
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="save-pricing-btn">
            {saving ? 'Saving...' : 'Save prices'}
          </Button>
        </div>
      </div>

      {hasOverrides && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-2.5 text-amber-300 text-sm">
          Custom price overrides are active — defaults are not in use.
        </div>
      )}

      {/* ── Plan Prices ─────────────────────────────────────────────────── */}
      <div className="bg-slate-800/50 border border-slate-700/40 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/40 flex items-center gap-2">
          <CreditCard size={15} className="text-indigo-400" />
          <h3 className="text-sm font-semibold text-white">Subscription Plans</h3>
        </div>
        <table className="w-full text-sm" data-testid="plan-price-table">
          <thead>
            <tr className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-700/40">
              <th className="px-5 py-2.5 text-left">Plan</th>
              <th className="px-5 py-2.5 text-left">Interval</th>
              <th className="px-5 py-2.5 text-right">Base price</th>
              <th className="px-5 py-2.5 text-right">Extra branch/mo</th>
              <th className="px-5 py-2.5 text-right">Extra branch/yr</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p, i) => {
              const meta = PLAN_DISPLAY[p.id] || { name: p.display_name, interval: p.interval, badge: 'bg-slate-700' };
              const edit = planEdits[p.id] || {};
              return (
                <tr key={p.id} className={`border-b border-slate-700/20 ${i % 2 === 0 ? '' : 'bg-slate-800/30'}`}>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${meta.badge} text-white`}>{meta.name}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-300">{meta.interval}</td>
                  <td className="px-5 py-3 text-right">
                    <PhpInput
                      value={edit.amount_php}
                      onChange={v => setPlanEdits(prev => ({ ...prev, [p.id]: { ...prev[p.id], amount_php: v } }))}
                      testId={`plan-price-${p.id}`}
                    />
                  </td>
                  <td className="px-5 py-3 text-right">
                    {p.has_extra_branch ? (
                      <PhpInput
                        value={edit.extra_branch_php}
                        onChange={v => setPlanEdits(prev => ({ ...prev, [p.id]: { ...prev[p.id], extra_branch_php: v } }))}
                        testId={`extra-branch-php-${p.id}`}
                      />
                    ) : <span className="text-slate-600 text-xs">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {p.has_extra_branch ? (
                      <PhpInput
                        value={edit.extra_branch_annual_php}
                        onChange={v => setPlanEdits(prev => ({ ...prev, [p.id]: { ...prev[p.id], extra_branch_annual_php: v } }))}
                        testId={`extra-branch-annual-php-${p.id}`}
                      />
                    ) : <span className="text-slate-600 text-xs">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Bundle Add-on Prices ─────────────────────────────────────────── */}
      <div className="bg-slate-800/50 border border-slate-700/40 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700/40 flex items-center gap-2">
          <Layers size={15} className="text-emerald-400" />
          <h3 className="text-sm font-semibold text-white">Add-on Bundle Prices</h3>
          <span className="text-slate-500 text-xs">(monthly rate — annual is 10× monthly)</span>
        </div>
        <table className="w-full text-sm" data-testid="bundle-price-table">
          <thead>
            <tr className="text-slate-500 text-xs uppercase tracking-wide border-b border-slate-700/40">
              <th className="px-5 py-2.5 text-left">Bundle</th>
              <th className="px-5 py-2.5 text-left">Description</th>
              <th className="px-5 py-2.5 text-right">Monthly price</th>
            </tr>
          </thead>
          <tbody>
            {bundles.map((b, i) => {
              const edit = bundleEdits[b.id] || {};
              return (
                <tr key={b.id} className={`border-b border-slate-700/20 ${i % 2 === 0 ? '' : 'bg-slate-800/30'}`}>
                  <td className="px-5 py-3 font-medium text-white whitespace-nowrap">{b.label}</td>
                  <td className="px-5 py-3 text-slate-400 text-xs max-w-xs">{b.description}</td>
                  <td className="px-5 py-3 text-right">
                    <PhpInput
                      value={edit.addon_price_php}
                      onChange={v => setBundleEdits(prev => ({ ...prev, [b.id]: { addon_price_php: v } }))}
                      testId={`bundle-price-${b.id}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-slate-600 text-xs">
        Prices are stored in <code className="text-slate-500">platform_settings.pricing_overrides</code>.
        The upgrade page and PayMongo checkout both read these values.
      </p>
    </div>
  );
}


/* ── Backups & Restore Panel ─────────────────────────────────────────────── */
function RestorePanel() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null); // backup record being restored
  const [restoreResult, setRestoreResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('pre_delete'); // 'all' | 'pre_delete'
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/superadmin/all-backups');
      setBackups(r.data || []);
    } catch { toast.error('Failed to load backups'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleRestoreFromR2 = async (backup) => {
    if (!window.confirm(`Restore "${backup.org_name || backup.org_id}"?\n\nThis will:\n• Recreate the company and all its data\n• Overwrite any existing data for this company\n\nContinue?`)) return;
    setRestoring(backup);
    setRestoreResult(null);
    try {
      const r = await api.post(`/superadmin/restore/${backup._id || backup.id}`);
      setRestoreResult({ success: true, data: r.data, org_name: backup.org_name });
      toast.success(`${backup.org_name || 'Company'} restored successfully`);
      load();
    } catch (e) {
      setRestoreResult({ success: false, error: e.response?.data?.detail || 'Restore failed', org_name: backup.org_name });
    }
    setRestoring(null);
  };

  const handleUploadRestore = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Select a .json.gz backup file'); return; }
    if (!window.confirm(`Restore from "${file.name}"?\n\nThis will recreate the company and all its data from this file.\n\nContinue?`)) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/superadmin/restore-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRestoreResult({ success: true, data: r.data, org_name: r.data.org_name });
      toast.success(`${r.data.org_name || 'Company'} restored from file`);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      setRestoreResult({ success: false, error: e.response?.data?.detail || 'Upload restore failed' });
    }
    setUploading(false);
  };

  const filtered = filter === 'pre_delete'
    ? backups.filter(b => b.backup_type === 'pre_delete' || b.deletion_completed_at)
    : backups;

  const fmtDate = (iso) => { try { return formatDateTime(iso); } catch { return iso; } };
  const fmtSize = (mb) => mb ? `${parseFloat(mb).toFixed(1)} MB` : '—';

  const typeLabel = (b) => {
    if (b.backup_type === 'pre_delete' || b.deletion_completed_at) return { label: 'Pre-Delete', cls: 'bg-red-500/20 text-red-300 border-red-500/30' };
    if (b.backup_type === 'restore') return { label: 'Restore Event', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/30' };
    if (b.triggered_by?.includes('schedule')) return { label: 'Scheduled', cls: 'bg-slate-500/20 text-slate-300 border-slate-600' };
    return { label: 'Manual', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' };
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-white font-semibold text-lg">Backups &amp; Restore</h2>
          <p className="text-slate-400 text-xs mt-0.5">Restore a deleted company or upload a backup file to recover data.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Filter toggle */}
          <div className="flex gap-1 bg-slate-800 rounded-lg p-1 border border-slate-700">
            {[['pre_delete', 'Deleted Companies'], ['all', 'All Backups']].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${filter === v ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                {l}
              </button>
            ))}
          </div>
          {/* Upload restore */}
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".gz,.json.gz" className="hidden" id="restore-file-input" />
            <label htmlFor="restore-file-input"
              className="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white text-xs font-medium transition-colors border border-slate-600"
              data-testid="restore-file-label">
              <Upload size={13} /> Upload Backup File
            </label>
            <Button size="sm" onClick={handleUploadRestore} disabled={uploading}
              className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5" data-testid="upload-restore-btn">
              {uploading ? <><RefreshCw size={12} className="animate-spin" /> Restoring…</> : 'Restore →'}
            </Button>
          </div>
          <button onClick={load} className="p-2 text-slate-400 hover:text-white">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Restore result banner */}
      {restoreResult && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${restoreResult.success ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
          {restoreResult.success ? <CheckCircle size={18} className="text-emerald-400 mt-0.5" /> : <XCircle size={18} className="text-red-400 mt-0.5" />}
          <div className="flex-1">
            {restoreResult.success ? (
              <>
                <p className="text-emerald-300 font-semibold text-sm">"{restoreResult.org_name}" restored successfully</p>
                <p className="text-emerald-400/70 text-xs mt-0.5">{restoreResult.data?.total_documents_restored?.toLocaleString()} documents · {restoreResult.data?.collections_restored} collections</p>
                {restoreResult.data?.errors?.length > 0 && (
                  <p className="text-amber-400 text-xs mt-1">⚠ {restoreResult.data.errors.length} minor errors: {restoreResult.data.errors.join(', ')}</p>
                )}
              </>
            ) : (
              <>
                <p className="text-red-300 font-semibold text-sm">Restore failed</p>
                <p className="text-red-400/70 text-xs mt-0.5">{restoreResult.error}</p>
              </>
            )}
          </div>
          <button onClick={() => setRestoreResult(null)} className="text-slate-500 hover:text-slate-300"><X size={14} /></button>
        </div>
      )}

      {/* Backup list */}
      {loading ? (
        <div className="text-center py-16 text-slate-500"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading backups…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-600 border border-slate-700/40 rounded-2xl">
          <HardDrive size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">{filter === 'pre_delete' ? 'No deleted company backups yet' : 'No backups found'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((b, i) => {
            const { label, cls } = typeLabel(b);
            const isRestoring = restoring?._id === b._id || restoring?.id === b.id;
            const canRestore = b.r2_key && label !== 'Restore Event';
            return (
              <div key={b._id || i} className="bg-slate-800/30 border border-slate-700/40 rounded-xl px-4 py-3 flex items-center gap-4 flex-wrap">
                {/* Org info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm truncate">{b.org_name || b.org_id}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${cls}`}>{label}</span>
                    {b.org_doc && <span className="text-xs text-emerald-400/70 border border-emerald-500/20 px-1.5 py-0.5 rounded">org saved</span>}
                  </div>
                  <div className="flex gap-4 mt-0.5 text-[10px] text-slate-500">
                    <span>{fmtDate(b.created_at)}</span>
                    {b.size_mb && <span>{fmtSize(b.size_mb)}</span>}
                    {b.total_documents != null && <span>{b.total_documents?.toLocaleString()} docs</span>}
                    {b.triggered_by && <span title={b.triggered_by}>by {b.triggered_by.split(' ')[0]}</span>}
                    {b.deletion_completed_at && <span className="text-red-400/70">deleted {fmtDate(b.deletion_completed_at)}</span>}
                  </div>
                </div>
                {/* Restore button */}
                {canRestore ? (
                  <Button size="sm" onClick={() => handleRestoreFromR2(b)} disabled={isRestoring}
                    className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5 shrink-0"
                    data-testid={`restore-btn-${i}`}>
                    {isRestoring ? <><RefreshCw size={11} className="animate-spin" /> Restoring…</> : <><RefreshCw size={11} /> Restore</>}
                  </Button>
                ) : (
                  <span className="text-xs text-slate-600 shrink-0">{label === 'Restore Event' ? 'Event log' : 'No R2 file'}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ── Main Component ─────────────────────────────────────────────────────── */
export default function SuperAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgBranches, setOrgBranches] = useState({});
  const [editModal, setEditModal] = useState(null); // org being edited
  const [deleteModal, setDeleteModal] = useState(null); // org being deleted
  const [paymentSettings, setPaymentSettings] = useState({});
  const [savingPayment, setSavingPayment] = useState(false);

  // Payment submissions + approve/reject
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [approveModal, setApproveModal] = useState(null); // { org, submission }
  const [rejectModal, setRejectModal] = useState(null);   // { org, submission }
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [approveForm, setApproveForm] = useState({ plan: 'basic', extra_branches: 0, subscription_expires_at: '', note: '' });
  const [proofPreview, setProofPreview] = useState(null); // image URL to preview

  // BIR Requests
  const [birRequests, setBirRequests] = useState([]);
  const [birLoading, setBirLoading] = useState(false);
  const [birReviewModal, setBirReviewModal] = useState(null); // the request being reviewed
  const [birVerified, setBirVerified] = useState({});
  const [birReviewerNotes, setBirReviewerNotes] = useState('');
  const [birRejectReason, setBirRejectReason] = useState('');
  const [birActionLoading, setBirActionLoading] = useState(false);
  const [birShowReject, setBirShowReject] = useState(false);
  const [notifSettings, setNotifSettings] = useState({ telegram_chat_id: '', telegram_bot_token_set: false, frontend_url: '', support_email: '' });
  const [notifBotToken, setNotifBotToken] = useState('');
  const [savingNotif, setSavingNotif] = useState(false);

  // Demo Provisioning state
  const [demos, setDemos] = useState([]);
  const [demosLoading, setDemosLoading] = useState(false);
  const [demoForm, setDemoForm] = useState({ prospect_name: '', prospect_email: '', preset: 'agricultural', ttl_days: 3, bir_strict_mode: false });
  const [demoGenerating, setDemoGenerating] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [demoActionLoading, setDemoActionLoading] = useState({});

  const loadDemos = useCallback(async () => {
    setDemosLoading(true);
    try {
      const r = await api.get('/superadmin/demo/list');
      setDemos(r.data.demos || []);
    } catch {}
    setDemosLoading(false);
  }, []);

  const generateDemo = async () => {
    if (!demoForm.prospect_name.trim() || !demoForm.prospect_email.trim()) {
      toast.error('Prospect name and email are required');
      return;
    }
    setDemoGenerating(true);
    setDemoResult(null);
    try {
      const r = await api.post('/superadmin/demo/generate', demoForm);
      setDemoResult(r.data);
      toast.success(`Demo created: ${r.data.admin_email}`);
      await loadDemos();
      setDemoForm({ prospect_name: '', prospect_email: '', preset: 'agricultural', ttl_days: 3, bir_strict_mode: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate demo');
    }
    setDemoGenerating(false);
  };

  const deleteDemo = async (orgId, name) => {
    if (!window.confirm(`Delete demo for "${name}"? This cannot be undone.`)) return;
    setDemoActionLoading(p => ({ ...p, [orgId]: 'deleting' }));
    try {
      await api.delete(`/superadmin/demo/${orgId}`);
      toast.success('Demo deleted');
      await loadDemos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setDemoActionLoading(p => ({ ...p, [orgId]: null }));
  };

  const extendDemo = async (orgId, name) => {
    setDemoActionLoading(p => ({ ...p, [orgId]: 'extending' }));
    try {
      const r = await api.post(`/superadmin/demo/${orgId}/extend`, { extra_days: 3 });
      toast.success(`Extended by 3 days — new expiry: ${r.data.new_expiry?.substring(0, 10)}`);
      await loadDemos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extend failed');
    }
    setDemoActionLoading(p => ({ ...p, [orgId]: null }));
  };

  const resetDemo = async (orgId, name) => {
    if (!window.confirm(`Reset demo data for "${name}"? All sales history will be wiped and re-seeded.`)) return;
    setDemoActionLoading(p => ({ ...p, [orgId]: 'resetting' }));
    try {
      await api.post(`/superadmin/demo/${orgId}/reset`);
      toast.success('Demo data reset successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setDemoActionLoading(p => ({ ...p, [orgId]: null }));
  };

  const loadBirRequests = useCallback(async () => {
    setBirLoading(true);
    try {
      const r = await api.get('/superadmin/bir-requests?status=all');
      setBirRequests(r.data.requests || []);
    } catch {}
    setBirLoading(false);
  }, []);

  const loadNotifSettings = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/settings/notifications');
      setNotifSettings(r.data);
    } catch {}
  }, []);

  const openBirReview = (req) => {
    setBirReviewModal(req);
    setBirVerified({
      verified_business_name: req.declared_business_name || '',
      verified_tin: req.declared_tin || '',
      verified_atp_number: req.declared_atp_number || '',
      verified_machine_serial: req.declared_machine_serial || '',
      verified_bir_permit_number: req.declared_bir_permit_number || '',
      verified_vat_type: req.declared_vat_type || 'non_vat',
    });
    setBirReviewerNotes('');
    setBirRejectReason('');
    setBirShowReject(false);
  };

  const submitBirReview = async (action) => {
    if (!birReviewModal) return;
    setBirActionLoading(true);
    try {
      await api.post(`/superadmin/bir-requests/${birReviewModal.id}/review`, {
        action,
        ...birVerified,
        verified_receipt_workflow: birReviewModal.declared_receipt_workflow || 'staged',
        senior_citizen_discount_enabled: birReviewModal.senior_citizen_discount_enabled || false,
        reviewer_notes: birReviewerNotes,
        rejection_reason: birRejectReason,
      });
      toast.success(action === 'approve' ? 'BIR Activated for branch!' : 'Application rejected');
      setBirReviewModal(null);
      loadBirRequests();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setBirActionLoading(false);
  };

  const saveNotifSettings = async () => {
    setSavingNotif(true);
    try {
      const payload = { telegram_chat_id: notifSettings.telegram_chat_id, frontend_url: notifSettings.frontend_url, support_email: notifSettings.support_email };
      if (notifBotToken) payload.telegram_bot_token = notifBotToken;
      await api.put('/superadmin/settings/notifications', payload);
      toast.success('Notification settings saved');
      setNotifBotToken('');
      loadNotifSettings();
    } catch (e) { toast.error('Failed to save'); }
    setSavingNotif(false);
  };

  const load = useCallback(async () => {
    if (!user?.is_super_admin) { navigate('/dashboard'); return; }
    setLoading(true);
    try {
      const [s, o] = await Promise.all([
        api.get('/superadmin/stats'),
        api.get('/superadmin/organizations'),
      ]);
      setStats(s.data);
      setOrgs(o.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [user, navigate]);

  const loadPayment = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/settings/payment');
      setPaymentSettings(r.data?.value || {});
    } catch {}
  }, []);

  useEffect(() => { load(); loadPayment(); loadBirRequests(); loadNotifSettings(); }, [load, loadPayment, loadBirRequests, loadNotifSettings]);

  const loadSubmissions = useCallback(async () => {
    setSubmissionsLoading(true);
    try {
      const r = await api.get('/superadmin/payment-submissions?status=all');
      setSubmissions(r.data.submissions || []);
    } catch {}
    setSubmissionsLoading(false);
  }, []);

  const handleApprove = async () => {
    if (!approveModal) return;
    setActionLoading(true);
    try {
      await api.post(`/superadmin/organizations/${approveModal.org.id}/approve-subscription`, {
        plan: approveForm.plan,
        extra_branches: parseInt(approveForm.extra_branches) || 0,
        subscription_expires_at: approveForm.subscription_expires_at || null,
        note: approveForm.note,
      });
      toast.success(`Subscription approved for ${approveModal.org.name}`);
      setApproveModal(null);
      load();
      loadSubmissions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) { toast.error('Reason is required'); return; }
    setActionLoading(true);
    try {
      await api.post(`/superadmin/organizations/${rejectModal.org.id}/reject-subscription`, {
        reason: rejectReason,
        plan: rejectModal.submission?.plan_requested || rejectModal.org.plan,
      });
      toast.success(`Rejection sent to ${rejectModal.org.name}`);
      setRejectModal(null);
      setRejectReason('');
      loadSubmissions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const loadOrgBranches = async (orgId) => {
    if (orgBranches[orgId]) return;
    try {
      const r = await api.get(`/superadmin/organizations/${orgId}/branches`);
      setOrgBranches(prev => ({ ...prev, [orgId]: r.data }));
    } catch {}
  };

  const toggleExpand = async (orgId) => {
    if (expandedOrg === orgId) { setExpandedOrg(null); return; }
    setExpandedOrg(orgId);
    await loadOrgBranches(orgId);
  };

  const filtered = orgs.filter(o => {
    const matchSearch = !search ||
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.owner_email?.toLowerCase().includes(search.toLowerCase());
    const matchPlan = filterPlan === 'all' || (o.effective_plan || o.plan) === filterPlan;
    return matchSearch && matchPlan;
  });

  if (!user?.is_super_admin) return null;

  return (
    <div className="min-h-screen bg-[#060D1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0A0F1C]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-300 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Shield size={14} className="text-white" />
            </div>
            <span className="text-white font-bold">Platform Admin</span>
            <span className="text-slate-600 text-xs hidden md:block">· AgriBooks</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => navigate('/sports/super-admin')}
              data-testid="sports-admin-link"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all"
              style={{ background: '#E6394614', border: '1px solid #E6394640', color: '#E63946' }}>
              <span style={{ fontSize: 11 }}>⚡</span> Sports Mode
            </button>
            <button onClick={() => navigate('/superadmin/orbitprint/orgs')}
              data-testid="orbitprint-admin-link"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all"
              style={{ background: '#E6394614', border: '1px solid #E6394640', color: '#E63946' }}>
              <span style={{ fontSize: 11 }}>🛰</span> OrbitPrint
            </button>
            <Button variant="ghost" size="sm" onClick={load} className="text-slate-400 hover:text-white gap-2">
              <RefreshCw size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/error-log')} className="text-slate-400 hover:text-white gap-2" data-testid="error-log-link">
              <AlertTriangle size={14} />
              <span className="hidden md:inline text-xs">Error Log</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          {/* flex-wrap + h-auto: ten sections no longer overflow/clip the pill row —
              they wrap to a second line on narrower screens. */}
          <TabsList className="bg-slate-800/50 border border-slate-700/50 p-1 rounded-xl flex flex-wrap h-auto justify-start">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5">
              Overview
            </TabsTrigger>
            <TabsTrigger value="organizations" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5">
              Organizations {orgs.length > 0 && <span className="ml-1.5 bg-slate-600 text-slate-300 text-xs px-1.5 py-0.5 rounded-full">{orgs.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5"
              onClick={loadSubmissions}>
              Payments
              {submissions.filter(s => s.status === 'pending').length > 0 && (
                <span className="ml-1.5 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {submissions.filter(s => s.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5">
              Feature Matrix
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5">
              Payment Settings
            </TabsTrigger>
            <TabsTrigger value="restore" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5">
              Backups &amp; Restore
            </TabsTrigger>
            <TabsTrigger value="bir" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5"
              onClick={() => loadBirRequests()}>
              BIR Requests
              {birRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-1.5 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {birRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="demos" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5"
              onClick={() => loadDemos()}>
              Demo Mgmt
            </TabsTrigger>
            <TabsTrigger value="capacity" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5"
              data-testid="capacity-tab-trigger">
              Capacity
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded-lg px-5"
              data-testid="pricing-tab-trigger">
              Pricing
            </TabsTrigger>
          </TabsList>

          {/* ── OVERVIEW ────────────────────────────────────────────────── */}
          <TabsContent value="overview" className="space-y-6">
            {loading ? (
              <div className="text-center py-16 text-slate-500">Loading...</div>
            ) : stats && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
                  <KpiCard icon={Building2}     label="Total Orgs"    value={stats.total_organizations} color="slate" />
                  <KpiCard icon={CheckCircle}   label="Active (Paid)" value={stats.active}              color="emerald" />
                  <KpiCard icon={Clock}         label="On Trial"      value={stats.trial}               color="blue" />
                  <KpiCard icon={Star}          label="Founders"      value={stats.founders || 0}       color="amber" />
                  <KpiCard icon={AlertTriangle} label="Expiring Soon" value={stats.expiring_soon}       sub="within 7 days" color="amber" />
                  <KpiCard icon={XCircle}       label="Suspended"     value={stats.suspended}           color="red" />
                  <KpiCard icon={Users}         label="Total Users"   value={stats.total_users}         color="indigo" />
                </div>

                {/* Plan breakdown */}
                <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                  <h3 className="text-white font-semibold mb-5 flex items-center gap-2">
                    <Layers size={16} className="text-slate-400" /> Plan Breakdown
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { plan: 'basic',    label: 'Basic',      color: '#64748b', price: '₱1,500' },
                      { plan: 'standard', label: 'Standard',   color: '#10b981', price: '₱4,000' },
                      { plan: 'pro',      label: 'Pro',        color: '#6366f1', price: '₱7,500' },
                      { plan: 'founders', label: '★ Founders', color: '#f59e0b', price: 'Lifetime' },
                    ].map(({ plan, label, color, price }) => {
                      const count = stats.by_plan?.[plan] || 0;
                      const maxVal = Math.max(...Object.values(stats.by_plan || {}), 1);
                      return (
                        <div key={plan} className="bg-slate-900/50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-slate-300 text-sm font-medium">{label}</span>
                            <span className="text-xs text-slate-500">{price}</span>
                          </div>
                          <div className="text-2xl font-bold text-white mb-2">{count}</div>
                          <div className="w-full bg-slate-800 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full transition-all" style={{ width: `${(count / maxVal) * 100}%`, background: color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Orgs needing attention */}
                {orgs.filter(o => ['grace_period', 'expired', 'suspended'].includes(o.effective_plan)).length > 0 && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5">
                    <h3 className="text-amber-300 font-semibold mb-4 flex items-center gap-2">
                      <AlertTriangle size={16} /> Needs Attention
                    </h3>
                    <div className="space-y-2">
                      {orgs.filter(o => ['grace_period', 'expired', 'suspended'].includes(o.effective_plan)).map(org => (
                        <div key={org.id} className="flex items-center justify-between bg-slate-900/50 rounded-xl px-4 py-3">
                          <div>
                            <span className="text-white text-sm font-medium">{org.name}</span>
                            <span className="text-slate-500 text-xs ml-2">{org.owner_email}</span>
                          </div>
                          <PlanBadge plan={org.effective_plan} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ── ORGANIZATIONS ────────────────────────────────────────────── */}
          <TabsContent value="organizations" className="space-y-4">
            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search company or email..."
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 pl-9 h-10" />
              </div>
              <div className="flex gap-1.5">
                {['all', 'trial', 'basic', 'standard', 'pro', 'founders', 'suspended'].map(p => (
                  <button key={p} onClick={() => setFilterPlan(p)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterPlan === p ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
                    {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16 text-slate-500">Loading organizations...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-500">No organizations found</div>
            ) : (
              <div className="space-y-2">
                {filtered.map(org => (
                  <OrgRow key={org.id} org={org} expanded={expandedOrg === org.id}
                    branches={orgBranches[org.id] || []}
                    onToggle={() => toggleExpand(org.id)}
                    onEdit={() => setEditModal(org)}
                    onDelete={() => setDeleteModal(org)}
                    onRefresh={load} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── FEATURE FLAGS ────────────────────────────────────────────── */}
          <TabsContent value="features">
            <FeatureFlagsPanel />
          </TabsContent>

          {/* ── PAYMENT SETTINGS ─────────────────────────────────────────── */}
          <TabsContent value="settings" className="space-y-5">
            <TaxConfigPanel />
            <PayMongoWebhookSetup />
            <PayMongoLiveTest />
            <PaymentSettingsPanel
              settings={paymentSettings}
              setSettings={setPaymentSettings}
              saving={savingPayment}
              setSaving={setSavingPayment}
            />
          </TabsContent>

          {/* ── BACKUPS & RESTORE ───────────────────────────────────────── */}
          <TabsContent value="restore" className="space-y-4">
            <RestorePanel />
          </TabsContent>

          {/* ── PAYMENT SUBMISSIONS ─────────────────────────────────────── */}
          <TabsContent value="payments" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-semibold text-lg">Payment Submissions</h2>
                <p className="text-slate-400 text-xs mt-0.5">Customer payment proofs awaiting review. Approve to activate their plan.</p>
              </div>
              <button onClick={loadSubmissions} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-700">
                <RefreshCw size={14} className={submissionsLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            {submissionsLoading ? (
              <div className="text-center py-12 text-slate-500">Loading submissions...</div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12 text-slate-600 border border-slate-700/40 rounded-2xl">
                <CreditCard size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No payment submissions yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Pending first */}
                {['pending', 'approved', 'rejected'].map(statusGroup => {
                  const grouped = submissions.filter(s => s.status === statusGroup);
                  if (!grouped.length) return null;
                  return (
                    <div key={statusGroup}>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        {statusGroup === 'pending' ? '⏳ Awaiting Review' : statusGroup === 'approved' ? '✓ Approved' : '✗ Rejected'}
                        {' '}({grouped.length})
                      </p>
                      <div className="space-y-2">
                        {grouped.map(sub => {
                          const org = orgs.find(o => o.id === sub.organization_id);
                          return (
                            <div key={sub.id} className={`rounded-2xl border p-4 ${
                              statusGroup === 'pending' ? 'bg-amber-500/5 border-amber-500/30' :
                              statusGroup === 'approved' ? 'bg-emerald-500/5 border-emerald-500/20' :
                              'bg-slate-800/30 border-slate-700/40'
                            }`}>
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-white font-semibold text-sm">{sub.org_name || org?.name || 'Unknown'}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      statusGroup === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                                      statusGroup === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                                      'bg-red-500/20 text-red-300'
                                    }`}>{statusGroup}</span>
                                    <span className="text-emerald-400 text-xs font-bold">₱{sub.amount?.toLocaleString()}</span>
                                    <span className="text-slate-400 text-xs">{sub.payment_method}</span>
                                  </div>
                                  <p className="text-slate-400 text-xs mt-0.5">{sub.owner_email} · Plan requested: <strong className="text-slate-300 capitalize">{sub.plan_requested}</strong></p>
                                  {sub.reference_number && <p className="text-slate-500 text-xs">Ref: {sub.reference_number}</p>}
                                  {sub.notes && <p className="text-slate-500 text-xs italic">{sub.notes}</p>}
                                  <p className="text-slate-600 text-[10px] mt-1">{formatDateTime(sub.submitted_at)}</p>
                                  {statusGroup === 'rejected' && sub.rejection_reason && (
                                    <p className="text-red-400 text-xs mt-1">Reason: {sub.rejection_reason}</p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2 shrink-0">
                                  {sub.proof_image && (
                                    <button onClick={() => setProofPreview(sub.proof_image)}
                                      className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 px-3 py-1.5 rounded-lg flex items-center gap-1">
                                      <Upload size={11} /> View Proof
                                    </button>
                                  )}
                                  {statusGroup === 'pending' && org && (
                                    <>
                                      <button onClick={() => {
                                        setApproveModal({ org, submission: sub });
                                        setApproveForm({ plan: sub.plan_requested || 'basic', extra_branches: 0, subscription_expires_at: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0], note: '' });
                                      }}
                                        className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
                                        <CheckCircle size={11} /> Approve
                                      </button>
                                      <button onClick={() => { setRejectModal({ org, submission: sub }); setRejectReason(''); }}
                                        className="text-xs bg-red-600/80 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
                                        <XCircle size={11} /> Reject
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ── BIR REQUESTS ─────────────────────────────────────────────── */}
          <TabsContent value="bir" className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-semibold text-lg">BIR Activation Requests</h2>
                <p className="text-slate-400 text-xs mt-0.5">Review and approve BIR compliance applications from companies.</p>
              </div>
              <button onClick={loadBirRequests} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-700">
                <RefreshCw size={14} className={birLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            {birLoading ? (
              <div className="text-center py-12 text-slate-500">Loading requests...</div>
            ) : birRequests.length === 0 ? (
              <div className="text-center py-12 text-slate-600 border border-slate-700/40 rounded-2xl">
                <p className="text-sm">No BIR requests yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {['pending', 'approved', 'rejected', 'withdrawn'].map(statusGroup => {
                  const grouped = birRequests.filter(r => r.status === statusGroup);
                  if (!grouped.length) return null;
                  return (
                    <div key={statusGroup}>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        {statusGroup === 'pending' ? '⏳ Awaiting Review' : statusGroup === 'approved' ? '✓ Approved' : statusGroup === 'rejected' ? '✗ Rejected' : '— Withdrawn'}
                        {' '}({grouped.length})
                      </p>
                      <div className="space-y-2">
                        {grouped.map(req => (
                          <div key={req.id} className={`rounded-2xl border p-4 ${
                            statusGroup === 'pending' ? 'bg-blue-500/5 border-blue-500/30' :
                            statusGroup === 'approved' ? 'bg-emerald-500/5 border-emerald-500/20' :
                            'bg-slate-800/30 border-slate-700/40'
                          }`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-white font-semibold text-sm">{req.org_name}</span>
                                  <span className="text-slate-400 text-xs">→</span>
                                  <span className="text-slate-300 text-xs">{req.branch_name}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    statusGroup === 'pending' ? 'bg-blue-500/20 text-blue-300' :
                                    statusGroup === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                                    'bg-red-500/20 text-red-300'
                                  }`}>{statusGroup}</span>
                                </div>
                                <p className="text-slate-400 text-xs mt-1">
                                  TIN: <span className="font-mono text-slate-300">{req.declared_tin || '—'}</span>
                                  {' · '}VAT: {req.declared_vat_type === 'vat_registered' ? 'VAT Reg.' : 'Non-VAT'}
                                  {' · '}Submitted by: {req.submitted_by_email}
                                </p>
                                {req.has_discrepancies && (
                                  <p className="text-amber-400 text-xs mt-0.5">{req.discrepancies?.length} field(s) corrected during review</p>
                                )}
                                {statusGroup === 'rejected' && req.rejection_reason && (
                                  <p className="text-red-400 text-xs mt-1">Reason: {req.rejection_reason}</p>
                                )}
                                <p className="text-slate-600 text-[10px] mt-1">{req.submitted_at ? new Date(req.submitted_at).toLocaleString() : ''}</p>
                              </div>
                              {statusGroup === 'pending' && (
                                <button
                                  data-testid={`bir-review-open-btn-${req.id}`}
                                  onClick={() => openBirReview(req)}
                                  className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 shrink-0"
                                >
                                  Review
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Telegram Notification Settings */}
            <div className="mt-6 pt-6 border-t border-slate-700/40">
              <h3 className="text-slate-300 font-semibold text-sm mb-3">BIR Alert Notifications (Telegram)</h3>
              <p className="text-slate-500 text-xs mb-3">
                Configure a Telegram bot to receive BIR review alerts with a magic link for quick approval.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Bot Token</label>
                  <input
                    type="password"
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={notifSettings.telegram_bot_token_set ? '••••••• (already set)' : 'New bot token...'}
                    value={notifBotToken}
                    onChange={e => setNotifBotToken(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Chat ID</label>
                  <input
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="-1001234567890"
                    value={notifSettings.telegram_chat_id}
                    onChange={e => setNotifSettings(p => ({ ...p, telegram_chat_id: e.target.value }))}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-500">Frontend URL (for magic link)</label>
                  <input
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="https://agri-books.com"
                    value={notifSettings.frontend_url}
                    onChange={e => setNotifSettings(p => ({ ...p, frontend_url: e.target.value }))}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-500">Support / Contact email (shown to customers in emails)</label>
                  <input
                    data-testid="superadmin-support-email-input"
                    type="email"
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="support@agri-books.com"
                    value={notifSettings.support_email}
                    onChange={e => setNotifSettings(p => ({ ...p, support_email: e.target.value }))}
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Appears in the footer ("contact us at …") and as the Reply-To of every customer email. Leave blank to use the system default.</p>
                </div>
              </div>
              <button
                onClick={saveNotifSettings}
                disabled={savingNotif}
                className="mt-3 text-xs bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg flex items-center gap-1.5"
              >
                {savingNotif ? <><RefreshCw size={11} className="animate-spin" /> Saving…</> : 'Save Notification Settings'}
              </button>
            </div>
          </TabsContent>

          {/* ── DEMO MANAGEMENT ────────────────────────────────────────────── */}
          <TabsContent value="demos" className="space-y-6" data-testid="demo-management-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-semibold text-lg flex items-center gap-2">
                  <FlaskConical size={18} className="text-emerald-400" /> Demo Management
                </h2>
                <p className="text-slate-400 text-xs mt-0.5">Provision time-limited demo orgs for prospects with pre-seeded data.</p>
              </div>
              <button onClick={loadDemos} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-700">
                <RefreshCw size={14} className={demosLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Generate form */}
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-5 space-y-4" data-testid="demo-generate-form">
              <p className="text-white font-semibold text-sm">Generate New Demo</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-slate-400">Prospect Name</label>
                  <input
                    className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    placeholder="Juan dela Cruz"
                    value={demoForm.prospect_name}
                    onChange={e => setDemoForm(p => ({ ...p, prospect_name: e.target.value }))}
                    data-testid="demo-prospect-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Prospect Email</label>
                  <input
                    type="email"
                    className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    placeholder="prospect@email.com"
                    value={demoForm.prospect_email}
                    onChange={e => setDemoForm(p => ({ ...p, prospect_email: e.target.value }))}
                    data-testid="demo-prospect-email"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Preset Type</label>
                  <select
                    className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={demoForm.preset}
                    onChange={e => setDemoForm(p => ({ ...p, preset: e.target.value }))}
                    data-testid="demo-preset-select"
                  >
                    <option value="agricultural">Agricultural Supply</option>
                    <option value="grocery">Grocery / Sari-sari</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="hardware">Hardware Supply</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400">TTL (days)</label>
                  <select
                    className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={demoForm.ttl_days}
                    onChange={e => setDemoForm(p => ({ ...p, ttl_days: parseInt(e.target.value) }))}
                    data-testid="demo-ttl-select"
                  >
                    {[1,2,3,5,7].map(d => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
              </div>
              {/* BIR Strict Mode option */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-600/50 bg-slate-800/30 cursor-pointer hover:bg-slate-800/50 transition-colors" data-testid="demo-bir-strict-toggle">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={demoForm.bir_strict_mode}
                    onChange={e => setDemoForm(p => ({ ...p, bir_strict_mode: e.target.checked }))}
                    className="w-4 h-4 accent-emerald-500"
                    data-testid="demo-bir-strict-checkbox"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                    BIR Strict Mode
                    <span className="text-[9px] bg-emerald-600/30 text-emerald-300 border border-emerald-600/40 px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">Accredited</span>
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Forces full BIR compliance: auto-issue SI/CI/OR on every transaction, hides toggle switches, VAT override disabled. Every sale appears in BIR Z-Report.</p>
                </div>
              </label>
              <button
                onClick={generateDemo}
                disabled={demoGenerating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="demo-generate-btn"
              >
                {demoGenerating ? <RefreshCw size={14} className="animate-spin" /> : <FlaskConical size={14} />}
                {demoGenerating ? 'Generating…' : 'Generate Demo Org'}
              </button>

              {/* Result card */}
              {demoResult && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-2" data-testid="demo-result-card">
                  <p className="text-emerald-300 font-semibold text-sm">Demo Created Successfully</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <span className="text-slate-400">Admin Email:</span>
                    <span className="text-white font-mono">{demoResult.admin_email}</span>
                    <span className="text-slate-400">Password:</span>
                    <span className="text-white font-mono">{demoResult.admin_password}</span>
                    <span className="text-slate-400">Preset:</span>
                    <span className="text-slate-300">{demoResult.preset_label}</span>
                    <span className="text-slate-400">Expires:</span>
                    <span className="text-slate-300">{demoResult.expires_at?.substring(0, 10)}</span>
                    <span className="text-slate-400">Email sent to:</span>
                    <span className="text-slate-300 flex items-center gap-1"><Mail size={11} />{demoResult.email_sent_to}</span>
                  </div>
                  <div className="pt-2 text-xs text-slate-500">
                    Team PINs: Admin <span className="font-mono text-white">0000</span> · Manager <span className="font-mono text-white">1111</span> · Cashiers <span className="font-mono text-white">2222 / 3333 / 4444</span>
                  </div>
                </div>
              )}
            </div>

            {/* Active demo orgs table */}
            <div className="space-y-2">
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Active Demo Organizations</p>
              {demosLoading ? (
                <div className="text-center py-8 text-slate-500 text-sm">Loading demos…</div>
              ) : demos.length === 0 ? (
                <div className="text-center py-8 text-slate-600 border border-slate-700/40 rounded-2xl text-sm">No demo orgs yet</div>
              ) : (
                <div className="space-y-2" data-testid="demo-list">
                  {demos.map(demo => {
                    const isExpired = demo.is_expired;
                    const expiresDate = demo.demo_expires_at?.substring(0, 10) || '—';
                    const actionState = demoActionLoading[demo.id];
                    return (
                      <div key={demo.id} className={`rounded-2xl border p-4 ${isExpired ? 'bg-red-500/5 border-red-500/20' : 'bg-slate-800/40 border-slate-700/40'}`} data-testid={`demo-row-${demo.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-semibold text-sm">{demo.prospect_name || demo.name}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${isExpired ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'}`}>
                                {isExpired ? 'Expired' : 'Active'}
                              </span>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                                {demo.preset || 'agricultural'}
                              </span>
                              {demo.bir_strict_mode && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-semibold">
                                  BIR Strict
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-xs">
                              <span className="text-slate-400">Email: <span className="text-slate-300 font-mono">{demo.prospect_email || '—'}</span></span>
                              <span className="text-slate-400">Admin: <span className="text-slate-300 font-mono">{demo.admin_email || '—'}</span></span>
                              <span className="text-slate-400 flex items-center gap-1">
                                <Calendar size={10} /> Expires: <span className={isExpired ? 'text-red-400' : 'text-slate-300'}>{expiresDate}</span>
                              </span>
                              <span className="text-slate-400">Users: <span className="text-slate-300">{demo.user_count}</span></span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => extendDemo(demo.id, demo.prospect_name || demo.name)}
                              disabled={!!actionState}
                              className="text-xs bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 px-3 py-1.5 rounded-lg border border-blue-500/30 flex items-center gap-1"
                              data-testid={`demo-extend-${demo.id}`}
                            >
                              {actionState === 'extending' ? <RefreshCw size={11} className="animate-spin" /> : <Calendar size={11} />}
                              +3 days
                            </button>
                            <button
                              onClick={() => resetDemo(demo.id, demo.prospect_name || demo.name)}
                              disabled={!!actionState}
                              className="text-xs bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 px-3 py-1.5 rounded-lg border border-amber-500/30 flex items-center gap-1"
                              data-testid={`demo-reset-${demo.id}`}
                            >
                              {actionState === 'resetting' ? <RefreshCw size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                              Reset
                            </button>
                            <button
                              onClick={() => deleteDemo(demo.id, demo.prospect_name || demo.name)}
                              disabled={!!actionState}
                              className="text-xs bg-red-500/15 hover:bg-red-500/25 text-red-300 px-3 py-1.5 rounded-lg border border-red-500/30 flex items-center gap-1"
                              data-testid={`demo-delete-${demo.id}`}
                            >
                              {actionState === 'deleting' ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── CAPACITY ────────────────────────────────────────────────── */}
          <TabsContent value="capacity" className="space-y-4" data-testid="capacity-tab-content">
            <CapacityPanel />
          </TabsContent>

          <TabsContent value="pricing" data-testid="pricing-tab-content">
            <PricingPanel />
          </TabsContent>

        </Tabs>
      </div>

      {/* ── BIR Review Modal ─────────────────────────────────────────────── */}
      {birReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setBirReviewModal(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">Review BIR Application</h3>
              <button onClick={() => setBirReviewModal(null)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-400">Company:</span> <span className="text-white font-medium">{birReviewModal.org_name}</span></div>
              <div><span className="text-slate-400">Branch:</span> <span className="text-white">{birReviewModal.branch_name}</span></div>
              <div><span className="text-slate-400">Submitted by:</span> <span className="text-slate-300">{birReviewModal.submitted_by_email}</span></div>
              <div><span className="text-slate-400">Notes:</span> <span className="text-slate-300 italic">{birReviewModal.applicant_notes || '—'}</span></div>
              {birReviewModal.documents?.length > 0 && (
                <div className="col-span-2 bg-slate-800/40 rounded-lg p-3 mt-1">
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-2">
                    {birReviewModal.documents.length} Document(s) Submitted
                  </p>
                  <div className="space-y-1">
                    {birReviewModal.documents.map(doc => (
                      <div key={doc.doc_id} className="flex items-center justify-between text-xs">
                        <span className="text-slate-300 truncate max-w-[200px]">{doc.filename}</span>
                        {doc.download_url
                          ? <a href={doc.download_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 ml-2 shrink-0">View</a>
                          : <span className="text-slate-500 ml-2">No URL</span>
                        }
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Field comparisons */}
            <div className="space-y-3">
              {[
                { label: 'Business Name (BIR)', declaredKey: 'declared_business_name', verifiedKey: 'verified_business_name' },
                { label: 'TIN', declaredKey: 'declared_tin', verifiedKey: 'verified_tin' },
                { label: 'ATP Number', declaredKey: 'declared_atp_number', verifiedKey: 'verified_atp_number' },
                { label: 'Machine Serial (MSN)', declaredKey: 'declared_machine_serial', verifiedKey: 'verified_machine_serial' },
                { label: 'BIR Permit Number', declaredKey: 'declared_bir_permit_number', verifiedKey: 'verified_bir_permit_number' },
              ].map(f => {
                const declared = birReviewModal[f.declaredKey] || '';
                const verified = birVerified[f.verifiedKey] || '';
                const changed = declared.toLowerCase().trim() !== verified.toLowerCase().trim() && declared.trim();
                return (
                  <div key={f.label} className={`rounded-lg border p-3 ${changed ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-700'}`}>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">{f.label}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[9px] text-slate-500 mb-1">Declared by company</p>
                        <p className="text-xs font-mono text-slate-300 bg-slate-800/50 rounded px-2 py-1">{declared || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-500 mb-1">Verified {changed && <span className="text-amber-400">(edited)</span>}</p>
                        <input
                          className={`w-full text-xs font-mono rounded border px-2 py-1 bg-slate-800 text-white focus:outline-none focus:ring-1 focus:ring-blue-500 ${changed ? 'border-amber-500/50' : 'border-slate-700'}`}
                          value={verified}
                          onChange={e => setBirVerified(p => ({ ...p, [f.verifiedKey]: e.target.value }))}
                        />
                      </div>
                    </div>
                    {changed && (
                      <p className="text-[9px] text-amber-400 mt-1.5">Pre-filled and submitted data differ — edited to match documents.</p>
                    )}
                  </div>
                );
              })}
              {/* VAT type */}
              <div className="rounded-lg border border-slate-700 p-3">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">VAT Type</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[9px] text-slate-500 mb-1">Declared</p>
                    <p className="text-xs bg-slate-800/50 rounded px-2 py-1 text-slate-300">
                      {birReviewModal.declared_vat_type === 'vat_registered' ? 'VAT Registered (12%)' : 'Non-VAT'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500 mb-1">Verified</p>
                    <select
                      className="w-full text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white"
                      value={birVerified.verified_vat_type}
                      onChange={e => setBirVerified(p => ({ ...p, verified_vat_type: e.target.value }))}
                    >
                      <option value="non_vat">Non-VAT Registered</option>
                      <option value="vat_registered">VAT Registered (12%)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviewer notes */}
            <div>
              <label className="text-xs text-slate-400">Reviewer Notes (optional)</label>
              <textarea
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={2}
                placeholder="Internal notes..."
                value={birReviewerNotes}
                onChange={e => setBirReviewerNotes(e.target.value)}
              />
            </div>

            {!birShowReject ? (
              <div className="flex gap-3">
                <button onClick={() => setBirShowReject(true)} disabled={birActionLoading}
                  className="flex-1 py-2.5 rounded-xl border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/10 flex items-center justify-center gap-1.5"
                  data-testid="reject-bir-sa-btn">
                  Reject
                </button>
                <button data-testid="approve-bir-sa-btn" onClick={() => submitBirReview('approve')} disabled={birActionLoading}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                  {birActionLoading ? <><RefreshCw size={13} className="animate-spin" /> Processing…</> : '✓ Approve & Activate'}
                </button>
              </div>
            ) : (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-red-300">Rejection reason (required)</p>
                <textarea
                  className="w-full bg-slate-800 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none"
                  rows={2}
                  placeholder="Tell the company what needs to be corrected..."
                  value={birRejectReason}
                  onChange={e => setBirRejectReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button onClick={() => setBirShowReject(false)} className="flex-1 py-2 rounded-lg border border-slate-600 text-slate-400 text-xs hover:bg-slate-800">Cancel</button>
                  <button onClick={() => submitBirReview('reject')} disabled={birActionLoading || !birRejectReason.trim()}
                    className="flex-1 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">
                    {birActionLoading ? 'Rejecting…' : 'Confirm Reject'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Proof of Payment Preview */}
      {proofPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setProofPreview(null)}>
          <div className="relative max-w-xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setProofPreview(null)} className="absolute -top-10 right-0 text-white/60 hover:text-white text-sm">✕ Close</button>
            <img src={proofPreview} alt="Payment proof" className="w-full rounded-2xl shadow-2xl" />
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-[#0E1628] border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <CheckCircle size={18} className="text-emerald-400" /> Approve Subscription
            </h3>
            <p className="text-slate-400 text-sm">
              Activating plan for <strong className="text-white">{approveModal.org.name}</strong>. An email will be sent to {approveModal.org.owner_email}.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400">Plan</label>
                <select value={approveForm.plan} onChange={e => setApproveForm(f => ({ ...f, plan: e.target.value }))}
                  className="w-full mt-1 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm">
                  {['basic', 'standard', 'pro', 'founders'].map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
              </div>
              {['basic', 'standard', 'pro'].includes(approveForm.plan) && (
                <div>
                  <label className="text-xs text-slate-400">Subscription Expires</label>
                  <input type="date" value={approveForm.subscription_expires_at}
                    onChange={e => setApproveForm(f => ({ ...f, subscription_expires_at: e.target.value }))}
                    className="w-full mt-1 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
              <div>
                <label className="text-xs text-slate-400">Note (optional)</label>
                <input type="text" value={approveForm.note}
                  onChange={e => setApproveForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="e.g. Payment confirmed via GCash"
                  className="w-full mt-1 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm placeholder:text-slate-600" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setApproveModal(null)} className="flex-1 py-2 rounded-xl border border-slate-700 text-slate-400 hover:text-white text-sm">Cancel</button>
              <button onClick={handleApprove} disabled={actionLoading}
                className="flex-1 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm flex items-center justify-center gap-2">
                {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />} Approve & Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-[#0E1628] border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <XCircle size={18} className="text-red-400" /> Reject Subscription Payment
            </h3>
            <p className="text-slate-400 text-sm">
              <strong className="text-white">{rejectModal.org.name}</strong> ({rejectModal.org.owner_email}) will receive a rejection email with your reason.
            </p>
            <div>
              <label className="text-xs text-slate-400">Reason for rejection *</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                placeholder="e.g. Payment amount does not match the plan price. Please resend the exact amount of ₱4,000 for the Standard plan."
                rows={3}
                className="w-full mt-1 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm placeholder:text-slate-600 resize-none" />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setRejectModal(null)} className="flex-1 py-2 rounded-xl border border-slate-700 text-slate-400 hover:text-white text-sm">Cancel</button>
              <button onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}
                className="flex-1 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <XCircle size={14} />} Reject & Notify Customer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit subscription modal */}
      {editModal && (
        <EditSubscriptionModal
          org={editModal}
          onClose={() => setEditModal(null)}
          onSaved={(updated) => {
            setOrgs(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
            setEditModal(null);
            toast.success('Subscription updated');
          }}
        />
      )}

      {/* Delete organization modal */}
      {deleteModal && (
        <DeleteOrgModal
          org={deleteModal}
          onClose={() => setDeleteModal(null)}
          onDeleted={() => {
            setOrgs(prev => prev.filter(o => o.id !== deleteModal.id));
            toast.success(`${deleteModal.name} has been deleted and backed up.`);
          }}
        />
      )}
    </div>
  );
}

/* ── Org Row ────────────────────────────────────────────────────────────── */
function OrgRow({ org, expanded, branches, onToggle, onEdit, onDelete, onRefresh }) {
  const effectivePlan = org.effective_plan || org.plan;
  const statusKey = org.subscription_status === 'active' && effectivePlan === 'grace_period'
    ? 'grace_period'
    : effectivePlan === 'expired' ? 'expired' : org.subscription_status;

  const expiryDate = org.plan === 'trial' ? org.trial_ends_at : org.subscription_expires_at;
  const daysLeft = expiryDate
    ? Math.ceil((new Date(expiryDate) - new Date()) / 86400000)
    : null;

  return (
    <div className="bg-slate-800/30 border border-slate-700/40 rounded-2xl overflow-hidden hover:border-slate-600/60 transition-colors">
      {/* Row header */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Status dot */}
        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${STATUS_DOT[statusKey] || 'bg-slate-500'}`} />

        {/* Company info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm truncate">{org.name}</span>
            {org.is_default && <span className="text-xs bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">Default</span>}
          </div>
          <div className="text-slate-500 text-xs mt-0.5 truncate">{org.owner_email || 'No email'}</div>
        </div>

        {/* Plan */}
        <div className="hidden sm:block">
          <PlanBadge plan={effectivePlan} />
        </div>

        {/* Branch gauge */}
        <div className="hidden md:block">
          <BranchGauge used={org.branch_count || 0} max={org.max_branches || 1} />
        </div>

        {/* Users */}
        <div className="hidden lg:flex items-center gap-1.5 text-slate-400">
          <Users size={13} />
          <span className="text-xs">{org.user_count || 0}</span>
        </div>

        {/* Expiry */}
        {org.plan === 'founders' ? (
          <div className="hidden lg:flex items-center gap-1 text-amber-300 text-xs font-medium">
            <span>★</span> Lifetime
          </div>
        ) : daysLeft !== null ? (
          <div className={`hidden lg:block text-xs font-medium ${daysLeft <= 0 ? 'text-red-400' : daysLeft <= 7 ? 'text-amber-400' : 'text-slate-500'}`}>
            {daysLeft <= 0 ? 'Expired' : `${daysLeft}d left`}
          </div>
        ) : ['basic', 'standard', 'pro'].includes(org.plan) ? (
          <div className="hidden lg:block text-xs text-amber-500/70" title="No expiry date set — plan won't auto-expire">
            No expiry set
          </div>
        ) : null}

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            data-testid={`view-as-org-${org.id}`}
            onClick={async () => {
              try {
                await api.post(`/superadmin/impersonate/${org.id}/enter`);
                toast.success(`Now viewing as ${org.name}`);
                window.location.href = '/dashboard';
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not enter tenant view');
              }
            }}
            title={`View as ${org.name} (4-hour audit-logged session)`}
            className="w-8 h-8 rounded-lg bg-amber-500/20 hover:bg-amber-500/40 text-amber-300 hover:text-amber-200 flex items-center justify-center transition-colors">
            <Eye size={14} />
          </button>
          <button data-testid={`edit-org-${org.id}`} onClick={onEdit}
            className="w-8 h-8 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 hover:text-white flex items-center justify-center transition-colors">
            <Edit3 size={14} />
          </button>
          <button data-testid={`delete-org-${org.id}`} onClick={onDelete}
            className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/25 text-red-500/60 hover:text-red-400 flex items-center justify-center transition-colors"
            title={`Delete ${org.name}`}>
            <Trash2 size={14} />
          </button>
          <button data-testid={`expand-org-${org.id}`} onClick={onToggle}
            className="w-8 h-8 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 hover:text-white flex items-center justify-center transition-colors">
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-slate-700/40 bg-slate-900/30 px-5 py-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Branches */}
            <div>
              <h4 className="text-slate-300 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
                <GitBranch size={13} /> Branches ({org.branch_count || 0}/{org.max_branches === 0 ? '∞' : org.max_branches})
              </h4>
              {branches.length === 0 ? (
                <p className="text-slate-600 text-xs">No branches yet</p>
              ) : (
                <div className="space-y-1.5">
                  {branches.map(b => (
                    <div key={b.id} className="flex items-center gap-2 text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full ${b.active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      <span className={b.active ? 'text-slate-300' : 'text-slate-600'}>{b.name}</span>
                      {b.is_main && <span className="text-xs text-emerald-500">main</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Subscription details */}
            <div>
              <h4 className="text-slate-300 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
                <CreditCard size={13} /> Subscription
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Plan</span>
                  <span className="text-slate-300 capitalize">{org.plan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Effective</span>
                  <span className="text-slate-300 capitalize">{effectivePlan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <span className="text-slate-300">{org.subscription_status}</span>
                </div>
                {org.trial_ends_at && org.plan === 'trial' && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Trial ends</span>
                    <span className="text-slate-300">{formatDate(org.trial_ends_at)}</span>
                  </div>
                )}
                {org.plan === 'founders' ? (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Expires</span>
                    <span className="text-amber-300 font-semibold">★ Never (Founders · Lifetime)</span>
                  </div>
                ) : org.billing_cycle === 'lifetime' ? (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Expires</span>
                    <span className="text-emerald-300 font-semibold">Never (Lifetime)</span>
                  </div>
                ) : org.subscription_expires_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sub expires</span>
                    <div className="flex items-center gap-1.5">
                      {org.billing_cycle && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${org.billing_cycle === 'annual' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}>
                          {org.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
                        </span>
                      )}
                      <span className={`${
                        new Date(org.subscription_expires_at) < new Date() ? 'text-red-400' :
                        Math.ceil((new Date(org.subscription_expires_at) - new Date()) / 86400000) <= 7 ? 'text-amber-400' :
                        'text-slate-300'
                      }`}>
                        {formatDate(org.subscription_expires_at)}
                        {' '}({Math.ceil((new Date(org.subscription_expires_at) - new Date()) / 86400000)}d)
                      </span>
                    </div>
                  </div>
                )}
                {['basic', 'standard', 'pro'].includes(org.plan) && !org.subscription_expires_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Expires</span>
                    <span className="text-amber-500/70 text-xs">Not set — set via Edit</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Max branches</span>
                  <span className="text-slate-300">{org.max_branches === 0 ? '∞' : org.max_branches} (+{org.extra_branches || 0} add-on)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Joined</span>
                  <span className="text-slate-300">{formatDate(org.created_at)}</span>
                </div>
                {org.admin_notes && (
                  <div className="mt-2 p-2 bg-slate-800 rounded-lg text-slate-400 text-xs">{org.admin_notes}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Delete Organization Modal ───────────────────────────────────────────── */
function DeleteOrgModal({ org, onClose, onDeleted }) {
  // step: 'confirm_name' → 'confirm_final' → 'working' → 'done' → 'error'
  const [step, setStep] = useState('confirm_name');
  const [nameInput, setNameInput] = useState('');
  const [finalInput, setFinalInput] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const nameMatch = nameInput.trim() === org.name.trim();
  const finalMatch = finalInput.trim() === 'PERMANENTLY DELETE';

  const runDelete = async () => {
    setStep('working');
    setError('');
    try {
      const r = await api.delete(`/superadmin/organizations/${org.id}`);
      setResult(r.data);
      setStep('done');
      onDeleted();
    } catch (e) {
      setError(e.response?.data?.detail || 'An unexpected error occurred.');
      setStep('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-[#0f172a] border border-red-500/30 rounded-2xl shadow-2xl overflow-hidden"
        data-testid="delete-org-modal">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-slate-700/50">
          <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
            <Trash2 size={18} className="text-red-400" />
          </div>
          <div>
            <p className="text-white font-bold text-base">Delete Company</p>
            <p className="text-slate-400 text-xs mt-0.5 truncate max-w-xs">{org.name}</p>
          </div>
          {step !== 'working' && (
            <button onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-300 p-1">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="px-6 py-5 space-y-4">

          {/* ── Step 1: Type company name ───────────────────────────────── */}
          {step === 'confirm_name' && (
            <>
              <div className="bg-red-500/8 border border-red-500/20 rounded-xl p-4 space-y-1.5">
                <p className="text-red-300 text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle size={15} /> This will permanently delete:
                </p>
                <ul className="text-red-400/80 text-xs space-y-1 ml-5 list-disc">
                  <li>All company data — products, inventory, sales, customers</li>
                  <li>All users, branches, purchase orders, reports</li>
                  <li>All documents, receipts, settings, SMS history</li>
                </ul>
                <p className="text-emerald-400 text-xs mt-2 flex items-center gap-1.5">
                  <CheckCircle size={12} /> A full backup will be saved to R2 before deletion.
                </p>
              </div>

              <div>
                <label className="text-slate-400 text-xs block mb-2">
                  Type the company name exactly to continue:
                  <span className="text-white ml-1.5 select-all">{org.name}</span>
                </label>
                <input
                  autoFocus
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  placeholder={org.name}
                  className="w-full h-10 bg-slate-800 border border-slate-600 rounded-lg px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                  data-testid="delete-name-input"
                />
                {nameInput && !nameMatch && (
                  <p className="text-red-400 text-xs mt-1">Name doesn't match — check capitalisation and spaces.</p>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <Button variant="ghost" onClick={onClose}
                  className="flex-1 h-10 text-slate-400 hover:text-white border border-slate-700 hover:bg-slate-800">
                  Cancel
                </Button>
                <Button onClick={() => { setStep('confirm_final'); setFinalInput(''); }}
                  disabled={!nameMatch}
                  className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                  data-testid="delete-next-btn">
                  Continue →
                </Button>
              </div>
            </>
          )}

          {/* ── Step 2: Final confirmation ──────────────────────────────── */}
          {step === 'confirm_final' && (
            <>
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 text-center space-y-1">
                <p className="text-white font-semibold text-sm">{org.name}</p>
                <p className="text-slate-400 text-xs">{org.owner_email}</p>
                <p className="text-slate-500 text-xs">{org.user_count || 0} users · {org.branch_count || 0} branches</p>
              </div>

              <div>
                <p className="text-slate-300 text-sm mb-1 font-medium">Final confirmation</p>
                <p className="text-slate-400 text-xs mb-3">
                  A backup will be created first, then all data will be permanently removed.<br />
                  Type <span className="text-white bg-slate-700 px-1.5 py-0.5 rounded text-xs">PERMANENTLY DELETE</span> to proceed.
                </p>
                <input
                  autoFocus
                  value={finalInput}
                  onChange={e => setFinalInput(e.target.value)}
                  placeholder="PERMANENTLY DELETE"
                  className="w-full h-10 bg-slate-800 border border-slate-600 rounded-lg px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                  data-testid="delete-final-input"
                />
                {finalInput && !finalMatch && (
                  <p className="text-red-400 text-xs mt-1">Must be exactly: PERMANENTLY DELETE</p>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <Button variant="ghost" onClick={() => setStep('confirm_name')}
                  className="flex-1 h-10 text-slate-400 hover:text-white border border-slate-700 hover:bg-slate-800">
                  ← Back
                </Button>
                <Button onClick={runDelete} disabled={!finalMatch}
                  className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40"
                  data-testid="delete-confirm-btn">
                  Backup & Delete
                </Button>
              </div>
            </>
          )}

          {/* ── Step 3: Working ─────────────────────────────────────────── */}
          {step === 'working' && (
            <div className="py-8 text-center space-y-4">
              <RefreshCw size={32} className="animate-spin text-red-400 mx-auto" />
              <div>
                <p className="text-white font-semibold">Processing…</p>
                <p className="text-slate-400 text-sm mt-1">Creating backup, then deleting all data.</p>
                <p className="text-slate-500 text-xs mt-2">This may take a few seconds. Do not close this window.</p>
              </div>
            </div>
          )}

          {/* ── Step 4: Done ────────────────────────────────────────────── */}
          {step === 'done' && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                <CheckCircle size={20} className="text-emerald-400 shrink-0" />
                <div>
                  <p className="text-emerald-300 font-semibold text-sm">Deleted successfully</p>
                  <p className="text-emerald-400/70 text-xs">{result.org_name}</p>
                </div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-2 text-xs">
                <p className="text-slate-300 font-semibold uppercase tracking-wider text-[10px]">Backup Details</p>
                <div className="flex justify-between">
                  <span className="text-slate-500">Filename</span>
                  <span className="text-slate-300 text-[10px]">{result.backup?.filename}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Size</span>
                  <span className="text-slate-300">{result.backup?.size_mb} MB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Documents backed up</span>
                  <span className="text-slate-300">{result.backup?.total_documents?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Stored in R2</span>
                  <span className={result.backup?.r2_uploaded ? 'text-emerald-400' : 'text-amber-400'}>
                    {result.backup?.r2_uploaded ? '✓ Uploaded' : '⚠ Local only'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Documents deleted</span>
                  <span className="text-red-400">{result.total_documents_deleted?.toLocaleString()}</span>
                </div>
              </div>
              <Button onClick={onClose} className="w-full h-10 bg-slate-700 hover:bg-slate-600 text-white">
                Close
              </Button>
            </div>
          )}

          {/* ── Step 5: Error ───────────────────────────────────────────── */}
          {step === 'error' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <XCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-semibold text-sm">Deletion failed</p>
                  <p className="text-red-400/80 text-xs mt-1">{error}</p>
                  <p className="text-slate-500 text-xs mt-2">No data was deleted — the operation was aborted before any changes.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={onClose}
                  className="flex-1 h-10 text-slate-400 border border-slate-700">Close</Button>
                <Button onClick={() => { setStep('confirm_final'); setFinalInput(''); }}
                  className="flex-1 h-10 bg-slate-700 hover:bg-slate-600 text-white">
                  Try Again
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}


/* ── Edit Subscription Modal ─────────────────────────────────────────────── */
function EditSubscriptionModal({ org, onClose, onSaved }) {
  // billing_cycle: "monthly" | "annual" | "lifetime"
  const detectCycle = () => {
    if (org.plan === 'founders') return 'lifetime';
    if (org.billing_cycle) return org.billing_cycle;
    return 'monthly';
  };

  const [form, setForm] = useState({
    plan: org.plan || 'trial',
    billing_cycle: detectCycle(),
    extra_branches: org.extra_branches || 0,
    trial_days: '',
    custom_date: '',   // only used when admin overrides the auto-calculated date
    notes: org.admin_notes || '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePlanChange = (newPlan) => {
    set('plan', newPlan);
    if (newPlan === 'founders') set('billing_cycle', 'lifetime');
  };

  // Auto-calculate expiry date preview from billing_cycle
  const calcExpiry = () => {
    if (form.billing_cycle === 'lifetime' || form.plan === 'founders') return null;
    if (form.custom_date) return form.custom_date;
    const days = form.billing_cycle === 'annual' ? 365 : 30;
    return new Date(Date.now() + days * 86400000).toISOString().split('T')[0];
  };
  const expiryPreview = calcExpiry();
  const daysUntilExpiry = expiryPreview
    ? Math.ceil((new Date(expiryPreview) - new Date()) / 86400000)
    : null;
  const isPaidPlan = ['basic', 'standard', 'pro', 'enterprise'].includes(form.plan);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        plan: form.plan,
        billing_cycle: form.billing_cycle,
        extra_branches: parseInt(form.extra_branches) || 0,
        notes: form.notes,
      };
      if (form.trial_days && parseInt(form.trial_days) > 0)
        payload.trial_days = parseInt(form.trial_days);
      // If admin set a custom date override, send it
      if (form.custom_date && form.billing_cycle !== 'lifetime' && isPaidPlan)
        payload.subscription_expires_at = form.custom_date;

      const r = await api.put(`/superadmin/organizations/${org.id}/subscription`, payload);
      onSaved(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setLoading(false);
  };

  const CYCLE_OPTIONS = [
    { key: 'monthly',  label: 'Monthly',  sub: '+30 days',  color: 'slate' },
    { key: 'annual',   label: 'Annual',   sub: '+365 days · 2 mo free', color: 'emerald', badge: '2 mo free' },
    { key: 'lifetime', label: 'Lifetime', sub: 'No expiry', color: 'amber' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-white font-bold text-lg">Edit Subscription</h3>
            <p className="text-slate-400 text-sm">{org.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-5">
          {/* Plan tier */}
          <div>
            <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2 block">Plan Tier</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'trial',     label: 'Trial',       color: 'blue' },
                { key: 'basic',     label: 'Starter',     color: 'slate' },
                { key: 'standard',  label: 'Business',    color: 'emerald' },
                { key: 'pro',       label: 'Professional',color: 'indigo' },
                { key: 'enterprise',label: 'Enterprise',  color: 'purple' },
                { key: 'founders',  label: '★ Founders',  color: 'amber', special: true },
                { key: 'suspended', label: 'Suspend',     color: 'red' },
              ].map(p => (
                <button key={p.key} onClick={() => handlePlanChange(p.key)}
                  className={`py-2.5 rounded-xl text-xs font-semibold border transition-all ${
                    form.plan === p.key
                      ? p.special
                        ? 'border-amber-400 bg-amber-400/15 text-amber-300'
                        : 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                      : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
            {form.plan === 'founders' && (
              <div className="mt-2 bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3 flex items-start gap-2">
                <span className="text-amber-300 text-lg">★</span>
                <div>
                  <p className="text-amber-300 text-xs font-semibold">Founders Plan — Lifetime · All Enterprise Features</p>
                  <p className="text-amber-400/70 text-xs mt-0.5">Reserved for early adopters. All features, no expiry, special designation.</p>
                </div>
              </div>
            )}
          </div>

          {/* Billing cycle — paid plans only, not founders (always lifetime) */}
          {isPaidPlan && (
            <div>
              <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2 block">Billing Cycle</label>
              <div className="grid grid-cols-3 gap-2">
                {CYCLE_OPTIONS.map(c => (
                  <button key={c.key} onClick={() => { set('billing_cycle', c.key); set('custom_date', ''); }}
                    className={`py-2.5 px-2 rounded-xl text-xs font-semibold border transition-all flex flex-col items-center gap-0.5 ${
                      form.billing_cycle === c.key
                        ? c.key === 'annual' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                        : c.key === 'lifetime' ? 'border-amber-400 bg-amber-400/10 text-amber-300'
                        : 'border-slate-500 bg-slate-700 text-white'
                        : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
                    }`}>
                    <span>{c.label}</span>
                    <span className={`text-[10px] font-normal ${form.billing_cycle === c.key ? 'opacity-80' : 'text-slate-600'}`}>{c.sub}</span>
                  </button>
                ))}
              </div>
              {form.billing_cycle === 'lifetime' && (
                <div className="mt-2 bg-amber-400/10 border border-amber-400/30 rounded-xl px-3 py-2">
                  <p className="text-amber-300 text-xs">Lifetime billing — no expiry. This tier's features are locked in permanently for this org.</p>
                </div>
              )}
              {form.billing_cycle !== 'lifetime' && (
                <div className="mt-2 space-y-1">
                  <div className={`text-xs flex items-center gap-1.5 ${
                    daysUntilExpiry !== null && daysUntilExpiry <= 0 ? 'text-red-400' :
                    daysUntilExpiry !== null && daysUntilExpiry <= 7 ? 'text-amber-400' : 'text-emerald-400'
                  }`}>
                    <Clock size={11} />
                    {form.custom_date
                      ? `Custom date: ${daysUntilExpiry} days (${expiryPreview})`
                      : form.billing_cycle === 'annual'
                        ? `Auto-set: +365 days (${expiryPreview})`
                        : `Auto-set: +30 days (${expiryPreview})`}
                  </div>
                  <button className="text-slate-600 text-[10px] hover:text-slate-400 underline"
                    onClick={() => set('custom_date', expiryPreview || '')}>
                    Override date manually →
                  </button>
                  {form.custom_date && (
                    <Input value={form.custom_date}
                      onChange={e => set('custom_date', e.target.value)}
                      type="date"
                      className="bg-slate-800 border-slate-700 text-white h-9 text-sm mt-1" />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Trial extension */}
          {form.plan === 'trial' && (
            <div>
              <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2 block">Extend Trial By (days)</label>
              <div className="flex gap-1.5 mb-2">
                {[7, 14, 30].map(d => (
                  <button key={d} onClick={() => set('trial_days', d)}
                    className="text-xs px-3 py-1.5 bg-slate-800 hover:bg-blue-600/50 text-slate-400 hover:text-white border border-slate-700 rounded-lg transition-all">
                    +{d} days
                  </button>
                ))}
              </div>
              <Input value={form.trial_days} onChange={e => set('trial_days', e.target.value)}
                placeholder="Custom days (e.g. 14)"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 h-9 text-sm" />
              {org.trial_ends_at && (
                <p className="text-slate-500 text-xs mt-1">Current trial ends: {formatDate(org.trial_ends_at)}</p>
              )}
            </div>
          )}

          {/* Extra branches */}
          {!['founders', 'suspended', 'trial'].includes(form.plan) && (
            <div>
              <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2 block">
                Extra Branch Add-ons <span className="text-slate-600 normal-case font-normal">(₱1,500/mo each)</span>
              </label>
              <div className="flex items-center gap-3">
                <button onClick={() => set('extra_branches', Math.max(0, form.extra_branches - 1))}
                  className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:text-white flex items-center justify-center">
                  <Minus size={14} />
                </button>
                <span className="text-white font-bold text-xl w-8 text-center">{form.extra_branches}</span>
                <button onClick={() => set('extra_branches', form.extra_branches + 1)}
                  className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:text-white flex items-center justify-center">
                  <Plus size={14} />
                </button>
                <span className="text-slate-500 text-xs">
                  = {(PLAN_LIMITS_MAP[form.plan] || 1) + form.extra_branches} total branches
                </span>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2 block">Admin Notes</label>
            <Input value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Internal notes (not visible to customer)"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 h-9 text-sm" />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleSave} disabled={loading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-10">
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={onClose}
            className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 h-10">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

const PLAN_LIMITS_MAP = { trial: 5, basic: 1, standard: 2, pro: 5, founders: 0, suspended: 0 };

/* ── PayMongo Webhook Setup ──────────────────────────────────────────────── */
function PayMongoWebhookSetup() {
  const [config, setConfig] = useState(null);
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/billing/webhook-config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const saveSecret = async () => {
    if (!secret.trim()) { toast.error('Enter the webhook secret from PayMongo dashboard'); return; }
    setSaving(true);
    try {
      await api.put('/billing/webhook-secret', { secret: secret.trim() });
      toast.success('Webhook secret saved! Signature verification is now active.');
      setConfig(c => ({ ...c, webhook_configured: true, secret_source: 'db' }));
      setSecret('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(config?.webhook_url || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5 space-y-4" data-testid="paymongo-webhook-setup">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            PayMongo Webhook Setup
            {config?.webhook_configured
              ? <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">CONFIGURED</span>
              : <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">SETUP REQUIRED</span>
            }
          </h3>
          <p className="text-slate-400 text-xs mt-0.5">
            Required for automatic subscription activation after payment.
          </p>
        </div>
      </div>

      {/* Step 1: Register in PayMongo */}
      <div className="bg-slate-900/50 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-slate-300 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 font-black text-[10px] flex items-center justify-center">1</span>
          Register this URL in PayMongo Dashboard → Developer Tools → Webhooks
        </p>
        <div className="flex gap-2">
          <div className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-mono text-emerald-300 break-all">
            {config?.webhook_url || 'Loading...'}
          </div>
          <button
            onClick={copyUrl}
            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold rounded-lg transition-colors"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <p className="text-[10px] text-slate-500">
          Events to subscribe: <span className="text-slate-300 font-mono">payment.paid</span> · <span className="text-slate-300 font-mono">payment_intent.succeeded</span>
        </p>
      </div>

      {/* Step 2: Enter webhook secret */}
      <div className="bg-slate-900/50 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-slate-300 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 font-black text-[10px] flex items-center justify-center">2</span>
          After creating the webhook, copy the secret key and paste it here
        </p>
        <div className="flex gap-2">
          <input
            type="password"
            placeholder="whsec_... or the webhook signing secret from PayMongo"
            value={secret}
            onChange={e => setSecret(e.target.value)}
            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
            data-testid="webhook-secret-input"
          />
          <button
            onClick={saveSecret}
            disabled={saving}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : 'Save Secret'}
          </button>
        </div>
        {config?.webhook_configured && (
          <p className="text-[10px] text-emerald-400">✓ Secret is already configured (source: {config.secret_source}). Paste a new one to update.</p>
        )}
      </div>

      <div className="text-[10px] text-slate-600 leading-relaxed">
        Without this webhook, customers must refresh the checkout page to confirm payment. With it, subscriptions activate within seconds of scanning the QR code.
      </div>
    </div>
  );
}


/* ── Tax Config (VAT rate + SC/PWD %) — platform legal constants ──────────── */
function TaxConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [vatRate, setVatRate] = useState('12');
  const [scpwd, setScpwd] = useState('20');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/superadmin/settings/tax-config').then(r => {
      setCfg(r.data);
      setVatRate(String(r.data.vat_rate));
      setScpwd(String(r.data.scpwd_discount_percent));
    }).catch(() => {});
  }, []);

  const save = async () => {
    const v = parseFloat(vatRate), s = parseFloat(scpwd);
    if (isNaN(v) || v < 0 || v > 20) { toast.error('VAT rate must be 0–20%'); return; }
    if (isNaN(s) || s < 0 || s > 50) { toast.error('SC/PWD discount must be 0–50%'); return; }
    setSaving(true);
    try {
      const r = await api.put('/superadmin/settings/tax-config', { vat_rate: v, scpwd_discount_percent: s });
      setCfg(r.data);
      toast.success('Tax config saved — applies platform-wide.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const dirty = cfg && (parseFloat(vatRate) !== cfg.vat_rate || parseFloat(scpwd) !== cfg.scpwd_discount_percent);

  return (
    <div className="bg-slate-800/30 border border-slate-700/40 rounded-2xl p-5 space-y-4" data-testid="tax-config-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-lg flex items-center gap-2"><Percent size={18} className="text-emerald-400" /> Tax Configuration</h2>
          <p className="text-slate-400 text-sm">National legal constants applied to ALL tenants. Tenants can only toggle these on/off per branch.</p>
        </div>
        <Button onClick={save} disabled={saving || !dirty}
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold gap-2 disabled:opacity-40">
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-slate-400 text-xs font-medium mb-1.5 block">Standard VAT Rate (%)</label>
          <Input type="number" step="0.5" min="0" max="20" value={vatRate}
            onChange={e => setVatRate(e.target.value)}
            className="bg-slate-900 border-slate-700 text-white h-9 text-sm" data-testid="tax-vat-rate-input" />
          <p className="text-[11px] text-slate-500 mt-1">PH standard is 12%. Shows dynamically on receipts &amp; BIR reports.</p>
        </div>
        <div>
          <label className="text-slate-400 text-xs font-medium mb-1.5 block">Senior Citizen / PWD Discount (%)</label>
          <Input type="number" step="1" min="0" max="50" value={scpwd}
            onChange={e => setScpwd(e.target.value)}
            className="bg-slate-900 border-slate-700 text-white h-9 text-sm" data-testid="tax-scpwd-input" />
          <p className="text-[11px] text-slate-500 mt-1">PH law (RA 9994 / RA 10754) mandates 20%.</p>
        </div>
      </div>
    </div>
  );
}

/* ── PayMongo LIVE QR Ph Self-Test ───────────────────────────────────────── */
function PayMongoLiveTest() {
  const [pesos, setPesos] = useState('1');
  const [creating, setCreating] = useState(false);
  const [test, setTest] = useState(null);     // { payment_intent_id, qr_image_url, amount_php }
  const [status, setStatus] = useState(null); // { paid, payment_id, net_amount, fee }
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPoll, []);

  const startTest = async () => {
    const amt = Math.round(parseFloat(pesos || '0') * 100);
    if (!amt || amt < 100) { toast.error('Minimum is ₱1.00'); return; }
    setCreating(true); setTest(null); setStatus(null);
    try {
      const r = await api.post('/superadmin/paymongo-live-test/create', { amount_centavos: amt });
      setTest(r.data);
      // begin polling live PI status
      stopPoll();
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get('/superadmin/paymongo-live-test/status', { params: { pi_id: r.data.payment_intent_id } });
          setStatus(s.data);
          if (s.data.paid) { stopPoll(); toast.success('Live QR Ph payment received! 🎉'); }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create live QR');
    }
    setCreating(false);
  };

  const reset = () => { stopPoll(); setTest(null); setStatus(null); };

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5 space-y-4" data-testid="paymongo-live-test">
      <div>
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <FlaskConical size={15} className="text-fuchsia-400" /> Live QR Ph Self-Test
          <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300">LIVE</span>
        </h3>
        <p className="text-slate-400 text-xs mt-0.5">
          Mint a real QR Ph code in your live PayMongo account to confirm payments land + reflect. Scan your own QR — the money lands in your own PayMongo balance.
        </p>
      </div>

      <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
        <AlertTriangle size={15} className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-amber-200/90 leading-relaxed">
          This charges <span className="font-bold">REAL money</span>. Scan it with <span className="font-bold">your own</span> GCash/bank so the ₱1 lands in your own PayMongo balance. Note: QR Ph payments <span className="font-bold">can't be auto-refunded</span> (PayMongo limitation) — withdraw your balance later via Payout.
        </p>
      </div>

      {!test ? (
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold block mb-1">Amount (₱)</label>
            <Input type="number" min="1" step="1" value={pesos} onChange={e => setPesos(e.target.value)}
              className="h-9 w-28 bg-slate-900 border-slate-700 text-white" data-testid="live-test-amount" />
          </div>
          <Button onClick={startTest} disabled={creating}
            className="h-9 bg-fuchsia-700 hover:bg-fuchsia-600 text-white" data-testid="live-test-generate-btn">
            {creating ? <><RefreshCw size={14} className="mr-1.5 animate-spin" /> Generating…</> : <><CreditCard size={14} className="mr-1.5" /> Generate Live QR</>}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="bg-white p-3 rounded-xl">
            {test.qr_image_url
              ? <img src={test.qr_image_url} alt="Live QR Ph" className="w-48 h-48 object-contain" data-testid="live-test-qr-image" />
              : <div className="w-48 h-48 flex items-center justify-center text-xs text-slate-500">No QR</div>}
          </div>
          <div className="text-center">
            <div className="text-white font-black text-lg">₱{Number(test.amount_php).toFixed(2)}</div>
            <div className="text-[10px] text-slate-500 font-mono">{test.payment_intent_id}</div>
          </div>

          {!status?.paid ? (
            <div className="flex items-center gap-2 text-amber-300 text-xs" data-testid="live-test-waiting">
              <RefreshCw size={13} className="animate-spin" /> Waiting for payment… scan with GCash/bank app
            </div>
          ) : (
            <div className="w-full space-y-2" data-testid="live-test-paid">
              <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm font-semibold">
                <CheckCircle size={16} /> Payment received & reflected!
              </div>
              <div className="text-[11px] text-slate-400 text-center">
                ₱{((status.net_amount || 0) / 100).toFixed(2)} landed in your PayMongo balance · fee ₱{((status.fee || 0) / 100).toFixed(2)}
              </div>
              <div className="flex items-start gap-2 bg-sky-500/10 border border-sky-500/30 rounded-lg p-2.5">
                <CheckCircle size={13} className="text-sky-400 mt-0.5 shrink-0" />
                <p className="text-[10.5px] text-sky-200/90 leading-relaxed">
                  Live QR Ph is working end-to-end. It's your own money (you scanned your own QR) — QR Ph can't be API-refunded, so withdraw it anytime via PayMongo <span className="font-semibold">Payout / Disbursement</span>.
                </p>
              </div>
            </div>
          )}

          <button onClick={reset} className="text-[11px] text-slate-500 hover:text-slate-300" data-testid="live-test-reset-btn">
            ✕ Close test
          </button>
        </div>
      )}
    </div>
  );
}



/* ── Feature Matrix Panel ──────────────────────────────────────────────────── */
function FeatureFlagsPanel() {
  const [defs, setDefs] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [resolved, setResolved] = useState({ basic: {}, standard: {}, pro: {}, enterprise: {} });
  const [dbOverrides, setDbOverrides] = useState({});
  const [localOverrides, setLocalOverrides] = useState({});
  const [reverseDeps, setReverseDeps] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [conflictDialog, setConflictDialog] = useState(null);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgList, setOrgList] = useState([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [orgConflictDialog, setOrgConflictDialog] = useState(null);  // per-org 409 conflict
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgOverrides, setOrgOverrides] = useState({});
  const [orgPlanFeatures, setOrgPlanFeatures] = useState({});
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgAddonBundles, setOrgAddonBundles] = useState([]);
  const [orgAddonSaving, setOrgAddonSaving] = useState(false);

  useEffect(() => {
    api.get('/superadmin/platform/feature-matrix').then(r => {
      setDefs(r.data.feature_definitions || []);
      setBundles(r.data.bundles || []);
      setResolved(r.data.resolved || {});
      setDbOverrides(r.data.db_overrides || {});
      setLocalOverrides(r.data.db_overrides || {});
      setReverseDeps(r.data.reverse_deps || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Live server-side search for org overrides panel (fixes 500-cap bug)
  useEffect(() => {
    if (!orgSearch.trim()) { setOrgList([]); return; }
    const timer = setTimeout(async () => {
      setOrgSearchLoading(true);
      try {
        const r = await api.get('/superadmin/organizations', { params: { search: orgSearch.trim(), limit: 20 } });
        setOrgList(r.data || []);
      } catch { setOrgList([]); }
      setOrgSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [orgSearch]);

  const plans = [
    { key: 'basic',      label: 'Starter',      price: '₱499',   color: '#C85A32' },
    { key: 'standard',   label: 'Business',     price: '₱1,499', color: '#477242' },
    { key: 'pro',        label: 'Professional', price: '₱2,999', color: '#C85A32' },
    { key: 'enterprise', label: 'Enterprise',   price: '₱4,999', color: '#2D3728' },
  ];

  const toggle = (plan, featureId) => {
    setLocalOverrides(prev => {
      const planOverrides = { ...(prev[plan] || {}) };
      const current = resolved[plan]?.[featureId] ?? false;
      planOverrides[featureId] = !current;
      return { ...prev, [plan]: planOverrides };
    });
    setResolved(prev => ({ ...prev, [plan]: { ...prev[plan], [featureId]: !(prev[plan]?.[featureId] ?? false) } }));
    setHasChanges(true);
  };

  const buildMatrix = () => {
    const matrix = {};
    for (const p of plans.map(p => p.key)) {
      const diff = localOverrides[p] || {};
      if (Object.keys(diff).length > 0) matrix[p] = diff;
    }
    return matrix;
  };

  const handleSave = async (force = false) => {
    setSaving(true);
    try {
      await api.put('/superadmin/platform/feature-matrix', { matrix: buildMatrix(), force });
      setHasChanges(false); setDbOverrides(localOverrides); setConflictDialog(null);
      toast.success('Feature matrix saved. Changes are live immediately.');
    } catch (e) {
      if (e.response?.status === 409) {
        const detail = e.response.data?.detail;
        if (detail?.code === 'dependency_conflict') setConflictDialog({ conflicts: detail.conflicts });
        else toast.error('Dependency conflict');
      } else toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleDisableBundle = async () => {
    if (!conflictDialog) return;
    const forceMatrix = buildMatrix();
    for (const c of conflictDialog.conflicts) {
      if (!forceMatrix[c.plan]) forceMatrix[c.plan] = {};
      for (const dep of c.blocked_by) forceMatrix[c.plan][dep] = false;
    }
    setSaving(true);
    try {
      await api.put('/superadmin/platform/feature-matrix', { matrix: forceMatrix, force: true });
      const newRes = { ...resolved };
      for (const [plan, changes] of Object.entries(forceMatrix)) {
        for (const [k, v] of Object.entries(changes)) { if (!newRes[plan]) newRes[plan] = {}; newRes[plan][k] = v; }
      }
      setResolved(newRes);
      setLocalOverrides(prev => {
        const next = { ...prev };
        for (const [plan, changes] of Object.entries(forceMatrix)) next[plan] = { ...(next[plan] || {}), ...changes };
        return next;
      });
      setHasChanges(false); setConflictDialog(null);
      toast.success('Bundle disabled — all dependents also disabled.');
    } catch { toast.error('Failed to disable bundle'); }
    setSaving(false);
  };

  const loadOrgOverrides = async (org) => {
    setSelectedOrg(org);
    const [or, br] = await Promise.all([
      api.get(`/superadmin/organizations/${org.id}/feature-overrides`),
      api.get(`/superadmin/organizations/${org.id}/addon-bundles`),
    ]);
    setOrgOverrides(or.data.feature_overrides || {});
    setOrgPlanFeatures(or.data.plan_features || {});
    setOrgAddonBundles(br.data.addon_bundles || []);
  };
  const saveOrgOverrides = async (force = false) => {
    if (!selectedOrg) return;
    setOrgSaving(true);
    try {
      await api.put(`/superadmin/organizations/${selectedOrg.id}/feature-overrides`, {
        feature_overrides: orgOverrides,
        force,
      });
      toast.success('Overrides saved');
      setOrgConflictDialog(null);
    } catch (e) {
      if (e.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        const conflicts = typeof detail === 'object' && detail.conflicts ? detail.conflicts : [];
        setOrgConflictDialog({
          conflicts,
          message: typeof detail === 'object' ? detail.message : String(detail),
        });
      } else {
        toast.error(e.response?.data?.detail || 'Failed to save overrides');
      }
    }
    setOrgSaving(false);
  };
  const saveOrgAddonBundles = async () => {
    if (!selectedOrg) return;
    setOrgAddonSaving(true);
    try { await api.put(`/superadmin/organizations/${selectedOrg.id}/addon-bundles`, { addon_bundles: orgAddonBundles }); toast.success('Add-on bundles saved'); }
    catch { toast.error('Failed'); }
    setOrgAddonSaving(false);
  };

  const categories = [...new Set(defs.filter(d => !d.always_on).map(d => d.category))];
  const addonBundles = bundles.filter(b => b.addon_eligible && !b.always_on);
  if (loading) return <div className="text-center py-12 text-slate-500">Loading feature matrix…</div>;

  return (
    <div className="space-y-6" data-testid="feature-matrix-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-white font-bold text-lg">Feature Matrix</h2>
          <p className="text-slate-400 text-sm mt-0.5">Toggle features per plan. Dependency conflicts show a Disable Bundle option.</p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && <span className="text-amber-400 text-xs flex items-center gap-1"><AlertTriangle size={12} /> Unsaved changes</span>}
          <Button onClick={() => handleSave(false)} disabled={saving || !hasChanges}
            className={`gap-1.5 text-sm ${hasChanges ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-slate-700 text-slate-500'}`}>
            <Save size={13} /> {saving ? 'Saving…' : 'Save & Publish'}
          </Button>
        </div>
      </div>
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 text-blue-300 text-xs">
        <strong>Dependency guard active:</strong> Disabling a parent feature while a child is enabled triggers a conflict dialog with a one-click Disable Bundle option.
      </div>

      <div className="bg-slate-800/20 border border-slate-700/40 rounded-2xl overflow-hidden">
        <div className="grid border-b border-slate-700/40" style={{ gridTemplateColumns: '1fr repeat(4, 140px)' }}>
          <div className="px-5 py-4 text-slate-400 text-xs font-semibold uppercase tracking-wider">Feature</div>
          {plans.map(p => (
            <div key={p.key} className="px-3 py-4 text-center border-l border-slate-700/30">
              <div className="font-bold text-sm" style={{ color: p.color }}>{p.label}</div>
              <div className="text-slate-500 text-[10px]">{p.price}/mo</div>
            </div>
          ))}
        </div>
        {categories.map(cat => (
          <div key={cat}>
            <div className="px-5 py-2.5 bg-slate-900/40 border-b border-slate-700/30">
              <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{cat}</span>
            </div>
            {defs.filter(d => d.category === cat && !d.always_on).map((feat, idx, arr) => {
              const revDeps = reverseDeps[feat.id] || [];
              return (
                <div key={feat.id}
                  className={`grid items-center hover:bg-slate-800/30 ${idx < arr.length - 1 ? 'border-b border-slate-700/20' : ''}`}
                  style={{ gridTemplateColumns: '1fr repeat(4, 140px)' }}>
                  <div className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-200 text-sm font-medium">{feat.label}</span>
                      {revDeps.length > 0 && (
                        <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-bold">
                          {revDeps.length} dep{revDeps.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {feat.depends_on?.length > 0 && <div className="text-slate-600 text-[10px] mt-0.5">Requires: {feat.depends_on.join(', ')}</div>}
                  </div>
                  {plans.map(p => {
                    const enabled = resolved[p.key]?.[feat.id] ?? false;
                    const depBlocked = (feat.depends_on || []).some(dep => !(resolved[p.key]?.[dep] ?? false));
                    const isDisabled = feat.always_on || depBlocked;
                    return (
                      <div key={p.key} className="px-3 py-3 flex justify-center border-l border-slate-700/20">
                        <button onClick={() => !isDisabled && toggle(p.key, feat.id)} disabled={isDisabled}
                          className={`relative w-10 h-5 rounded-full transition-all ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${enabled ? 'bg-emerald-500' : 'bg-slate-700'}`}
                          data-testid={`feat-toggle-${p.key}-${feat.id}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="bg-slate-800/30 border border-slate-700/40 rounded-2xl p-5 space-y-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          Add-on Bundles Catalog
          <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">{addonBundles.length} bundles</span>
        </h3>
        <p className="text-slate-400 text-xs">Standalone bundles purchasable on any plan tier. Enable per-org below.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {addonBundles.map(b => (
            <div key={b.id} className="bg-slate-900/50 border border-slate-700/40 rounded-xl p-3" data-testid={`bundle-card-${b.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-white text-xs">{b.label}</span>
                {b.addon_price_php && <span className="text-[10px] font-black text-emerald-400">₱{b.addon_price_php.toLocaleString()}/mo</span>}
              </div>
              <p className="text-slate-500 text-[10px] leading-snug mb-1">{b.description}</p>
              <div className="flex flex-wrap gap-1">
                {b.features.slice(0, 3).map(f => <span key={f} className="text-[9px] text-slate-600 bg-slate-800 px-1 py-0.5 rounded">{f}</span>)}
                {b.features.length > 3 && <span className="text-[9px] text-slate-600">+{b.features.length - 3} more</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-slate-800/30 border border-slate-700/40 rounded-2xl p-5 space-y-4">
        <h3 className="text-white font-semibold text-base">Per-Organization Overrides &amp; Add-ons</h3>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-9 pr-10 py-2 text-sm text-white placeholder-slate-600"
            placeholder="Search organization by name or email…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} data-testid="org-feature-search" />
          {orgSearchLoading && <RefreshCw size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-500" />}
        </div>
        {orgSearch && (
          <div className="bg-slate-900 rounded-xl border border-slate-700/50 max-h-48 overflow-y-auto">
            {orgList.length === 0 && !orgSearchLoading && (
              <div className="px-4 py-3 text-slate-500 text-sm">No organizations found for "{orgSearch}"</div>
            )}
            {orgList.map(org => (
              <button key={org.id} onClick={() => { loadOrgOverrides(org); setOrgSearch(''); }}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-800 text-left">
                <div>
                  <span className="text-white text-sm">{org.name}</span>
                  {org.owner_email && <span className="text-slate-600 text-xs ml-2">{org.owner_email}</span>}
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  org.effective_plan === 'founders' ? 'bg-amber-500/20 text-amber-300' :
                  org.effective_plan === 'pro' ? 'bg-indigo-500/20 text-indigo-300' :
                  'bg-slate-700 text-slate-400'
                }`}>{org.effective_plan || org.plan}</span>
              </button>
            ))}
          </div>
        )}
        {selectedOrg && (
          <div className="border border-slate-700/50 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-900/60 border-b border-slate-700/40 flex items-center justify-between">
              <span className="text-white font-semibold text-sm">{selectedOrg.name} <span className="text-slate-500 text-xs">({selectedOrg.effective_plan || selectedOrg.plan})</span></span>
              <div className="flex gap-2 items-center">
                {/* BIR Strict Mode toggle */}
                <button
                  onClick={async () => {
                    const newVal = !selectedOrg.bir_strict_mode;
                    try {
                      await api.put(`/superadmin/organizations/${selectedOrg.id}/bir-strict-mode`, { enabled: newVal });
                      setSelectedOrg(p => ({ ...p, bir_strict_mode: newVal }));
                      toast.success(`BIR Strict Mode ${newVal ? 'enabled' : 'disabled'} for ${selectedOrg.name}`);
                    } catch { toast.error('Failed to update BIR Strict Mode'); }
                  }}
                  className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-all ${selectedOrg.bir_strict_mode ? 'bg-emerald-600/30 border-emerald-600 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}
                  data-testid="org-bir-strict-toggle"
                  title="Toggle BIR Strict Mode"
                >
                  {selectedOrg.bir_strict_mode ? '✓ BIR Strict' : '+ BIR Strict'}
                </button>
                <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600 text-slate-300" onClick={() => setOrgOverrides({})}>Clear</Button>
                <Button size="sm" onClick={() => saveOrgOverrides()} disabled={orgSaving} className="h-7 text-xs bg-emerald-700 text-white">{orgSaving ? '…' : 'Save Overrides'}</Button>
              </div>
            </div>
            <div className="px-4 py-3 border-b border-slate-700/30">
              <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">Add-on Bundles</p>
              <div className="flex flex-wrap gap-2">
                {addonBundles.map(b => {
                  const on = orgAddonBundles.includes(b.id);
                  return (
                    <button key={b.id}
                      onClick={() => setOrgAddonBundles(p => on ? p.filter(x => x !== b.id) : [...p, b.id])}
                      className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-all ${on ? 'bg-emerald-600/30 border-emerald-600 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}
                      data-testid={`org-addon-toggle-${b.id}`}>
                      {on ? '✓ ' : '+ '}{b.label}{b.addon_price_php ? ' ₱' + b.addon_price_php.toLocaleString() : ''}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={saveOrgAddonBundles} disabled={orgAddonSaving} className="h-7 text-xs bg-blue-700 text-white">{orgAddonSaving ? '…' : 'Save Add-ons'}</Button>
              </div>
            </div>
            <div className="divide-y divide-slate-700/30 max-h-60 overflow-y-auto">
              {defs.filter(d => !d.always_on).map(feat => {
                const planVal = orgPlanFeatures[feat.id] ?? false;
                const override = orgOverrides[feat.id];
                const effectiveVal = override !== undefined ? override : planVal;
                // State: plan-on, plan-off, override-on (was off), override-off (was on)
                const isOverriddenOn  = override === true  && !planVal;
                const isOverriddenOff = override === false && planVal;
                const hasOverride = override !== undefined;
                return (
                  <div key={feat.id} className={`flex items-center justify-between px-4 py-2 hover:bg-slate-800/30 ${isOverriddenOff ? 'bg-orange-900/10' : isOverriddenOn ? 'bg-yellow-900/10' : ''}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        isOverriddenOff ? 'bg-orange-400' :
                        isOverriddenOn  ? 'bg-yellow-400' :
                        planVal ? 'bg-emerald-400' : 'bg-slate-600'
                      }`} />
                      <span className="text-slate-300 text-xs truncate">{feat.label}</span>
                      {isOverriddenOn  && <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-bold shrink-0">+Override</span>}
                      {isOverriddenOff && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 font-bold shrink-0">–Override</span>}
                      {!hasOverride && planVal && <span className="text-[9px] text-slate-600 shrink-0">plan</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {hasOverride && (
                        <button className="text-slate-600 text-[10px] hover:text-slate-400"
                          onClick={() => setOrgOverrides(p => { const n = {...p}; delete n[feat.id]; return n; })}>
                          Reset
                        </button>
                      )}
                      <button onClick={() => setOrgOverrides(p => ({ ...p, [feat.id]: !effectiveVal }))}
                        className={`relative w-9 h-4 rounded-full cursor-pointer transition-colors ${effectiveVal ? 'bg-emerald-500' : 'bg-slate-700'}`}
                        data-testid={`org-feat-toggle-${feat.id}`}>
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${effectiveVal ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {Object.keys(orgOverrides).length > 0 && (
              <div className="px-4 py-2 border-t border-slate-700/40">
                <button
                  onClick={() => { if (window.confirm('Reset ALL feature overrides for this org? They will revert to plan defaults.')) setOrgOverrides({}); }}
                  className="text-xs text-red-400 hover:text-red-300 font-medium">
                  Reset all {Object.keys(orgOverrides).length} override(s) to plan defaults
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {conflictDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="disable-bundle-dialog">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base">Dependency Conflict</h3>
                <p className="text-slate-400 text-xs">Cannot disable — other features still depend on this</p>
              </div>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 space-y-2 text-xs">
              {conflictDialog.conflicts.map((c, i) => (
                <div key={i}>
                  <p className="text-amber-300 font-semibold">Cannot disable "{c.feature_label}" on {c.plan}</p>
                  <p className="text-slate-400">Still enabled: {(c.blocked_by_labels || c.blocked_by || []).join(', ')}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-300 text-sm">Click <strong>Disable Bundle</strong> to also disable all dependent features at once.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => setConflictDialog(null)}>Cancel</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold" onClick={handleDisableBundle} disabled={saving} data-testid="disable-bundle-confirm-btn">
                {saving ? 'Disabling…' : 'Disable Bundle'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Per-org override conflict dialog */}
      {orgConflictDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="org-override-conflict-dialog">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base">Feature Dependency Conflict</h3>
                <p className="text-slate-400 text-xs">Disabling these features will also disable their dependents</p>
              </div>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 space-y-2 text-xs max-h-48 overflow-y-auto">
              {orgConflictDialog.message && (
                <p className="text-amber-300 text-sm font-medium mb-2">{orgConflictDialog.message}</p>
              )}
              {(orgConflictDialog.conflicts || []).map((c, i) => (
                <div key={i} className="border-b border-slate-700 pb-1 last:border-0">
                  <p className="text-amber-300 font-semibold">"{c.feature_label}"</p>
                  <p className="text-slate-400">Still on for this org: {(c.blocked_by || []).join(', ')}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-300 text-sm">
              Click <strong>Force Disable</strong> to override and also disable all conflicting dependent features for this org.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => setOrgConflictDialog(null)}>Cancel</Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold"
                onClick={() => saveOrgOverrides(true)}
                disabled={orgSaving}
                data-testid="org-override-force-btn"
              >
                {orgSaving ? 'Saving…' : 'Force Disable'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/* ── Payment Settings Panel ─────────────────────────────────────────────── */
function PaymentSettingsPanel({ settings, setSettings, saving, setSaving }) {
  const fileRefs = { gcash: useRef(), maya: useRef(), bank: useRef(), paypal: useRef() };

  const update = (method, key, value) => {
    setSettings(prev => ({
      ...prev,
      [method]: { ...(prev[method] || {}), [key]: value }
    }));
  };

  const handleFileUpload = (method, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File too large. Max 2MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => update(method, 'qr_base64', ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeQR = (method) => update(method, 'qr_base64', '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/settings/payment', { value: settings });
      toast.success('Payment settings saved!');
    } catch {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const METHODS = [
    { key: 'gcash', label: 'GCash', icon: '💚', color: 'emerald', fields: [
      { field: 'number', label: 'GCash Number', placeholder: '09XX-XXX-XXXX' },
      { field: 'account_name', label: 'Account Name', placeholder: 'Full name on account' },
    ]},
    { key: 'maya', label: 'Maya', icon: '💜', color: 'purple', fields: [
      { field: 'number', label: 'Maya Number', placeholder: '09XX-XXX-XXXX' },
      { field: 'account_name', label: 'Account Name', placeholder: 'Full name on account' },
    ]},
    { key: 'bank', label: 'Bank Transfer', icon: '🏦', color: 'blue', fields: [
      { field: 'bank_name', label: 'Bank Name', placeholder: 'e.g. BDO, BPI, UnionBank' },
      { field: 'account_number', label: 'Account Number', placeholder: 'XXXX-XXXX-XXXX' },
      { field: 'account_name', label: 'Account Name', placeholder: 'Business name on account' },
    ]},
    { key: 'paypal', label: 'PayPal', icon: '🔵', color: 'indigo', fields: [
      { field: 'email', label: 'PayPal Email', placeholder: 'paypal@email.com' },
      { field: 'link', label: 'PayPal.me Link', placeholder: 'https://paypal.me/yourlink' },
    ]},
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-lg">Payment Methods</h2>
          <p className="text-slate-400 text-sm">Configure how customers pay for subscriptions. QR codes appear on the Upgrade page.</p>
        </div>
        <Button onClick={handleSave} disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold gap-2">
          <Save size={14} /> {saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {METHODS.map(({ key, label, icon, fields }) => (
          <div key={key} className="bg-slate-800/30 border border-slate-700/40 rounded-2xl p-5 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{icon}</span>
              <h3 className="text-white font-semibold">{label}</h3>
            </div>

            {/* Text fields */}
            {fields.map(({ field, label: fLabel, placeholder }) => (
              <div key={field}>
                <label className="text-slate-400 text-xs font-medium mb-1.5 block">{fLabel}</label>
                <Input
                  value={settings[key]?.[field] || ''}
                  onChange={e => update(key, field, e.target.value)}
                  placeholder={placeholder}
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 h-9 text-sm"
                />
              </div>
            ))}

            {/* QR code upload */}
            <div>
              <label className="text-slate-400 text-xs font-medium mb-2 block">QR Code Image</label>
              {settings[key]?.qr_base64 ? (
                <div className="relative inline-block">
                  <img src={settings[key].qr_base64} alt={`${label} QR`}
                    className="w-32 h-32 rounded-xl border border-slate-600 object-contain bg-white p-1" />
                  <button onClick={() => removeQR(key)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center">
                    <Trash2 size={11} className="text-white" />
                  </button>
                  <button onClick={() => fileRefs[key]?.current?.click()}
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-slate-700 hover:bg-slate-600 text-xs text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                    Change
                  </button>
                </div>
              ) : (
                <button onClick={() => fileRefs[key]?.current?.click()}
                  className="w-32 h-32 border-2 border-dashed border-slate-700 hover:border-emerald-500/50 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-emerald-400 transition-colors group">
                  <Upload size={20} />
                  <span className="text-xs">Upload QR</span>
                </button>
              )}
              <input ref={fileRefs[key]} type="file" accept="image/*" className="hidden"
                onChange={e => handleFileUpload(key, e)} />
              <p className="text-slate-600 text-xs mt-2">PNG/JPG · Max 2MB</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
        <p className="text-blue-300 text-sm font-medium mb-1">How this works</p>
        <p className="text-blue-400/70 text-xs leading-relaxed">
          Customers see these payment details when they click "Upgrade" in the app. They send payment manually,
          then contact you for activation. You then activate their plan from this admin panel.
        </p>
      </div>
    </div>
  );
}
